import './styles.css';
import Alpine from 'alpinejs';
import {
  drainQueue, HOUSEHOLD_FIELD, listFilterKey, mergeForList, pendingForList,
  serverRowsOnFailure, splitQueue, syncResultMessages, toQueueRecord,
  type DrainResult, type ListCache, type QueueStore,
} from './offline-queue';
import {
  evalCalcExpr as evaluateCalcExpression,
  hasCalcOperator as containsCalcOperator,
} from './calc';
import { apiRequest, defaultDeps, settleAuthFirst, shouldClearForUpgrade } from './api-client';
import { loadFormCache, localStorageStore, saveFormCache } from './form-cache';
import { isOnline } from './connectivity';
import { isValidCatalog, isValidHousehold, loadStartup, sameSession, type SessionSnapshot, type StartupOutcome } from './startup-load';
import { createAuthAttempts, createSessionReload, type Persistence } from './session-reload';
import { newId } from './uuid';
import { createAuthStore, TOKEN_KEY, HOUSEHOLD_KEY } from './auth-store';

const authStore = createAuthStore({ native: false, ls: localStorage }); // ponytail: 原生建置在 §3.1 改成 { native: Capacitor.isNativePlatform(), secure: <Keychain/Keystore 外掛轉接> }；外掛選型與維護狀態在 §3.1 工單核對（規格 C4 甲）

declare const __APP_VERSION__: string;
declare const __BUILD_SHA__: string;

// API base URL
const API_BASE = import.meta.env.PROD
  ? 'https://bookkeeper-api.tom-e31.workers.dev'
  : 'http://localhost:8787';
const API_DEPS = defaultDeps(API_BASE);
const sessionReload = createSessionReload({ reload: () => location.reload() });
const authAttempts = createAuthAttempts({
  snapshot: sessionSnapshot,
  same: sameSession,
  writesAllowed: () => sessionReload.writesAllowed(),
});

// --- IndexedDB offline queue ---
const DB_NAME = 'bookkeeper-offline';
const STORE_NAME = 'queue';
let lastListLoad: ListCache = null;
let listCacheEpoch = 0;
let offlineQueueDrainPromise: Promise<DrainResult> | null = null;
let startupGeneration = 0;
// Ids deleted on this page. An add POST for such a row may still be in flight; its offline fallback must not
// re-queue a row the user has since deleted, or the next drain would resurrect it (R4 external review P1).
const deletedThisSession = new Set<string>();

// The list cache is a snapshot of the server; any successful write makes it stale.
function invalidateListCache() {
  lastListLoad = null;
  listCacheEpoch++;
}

function currentHouseholdId(): string | null {
  return authStore.getSession().householdId;
}

function sessionSnapshot(): SessionSnapshot {
  const { token, householdId: household } = authStore.getSession();
  return { token, household };
}

function openOfflineDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function enqueueOffline(tx: any, household: string | null) {
  const db = await openOfflineDB();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
      const store = transaction.objectStore(STORE_NAME);
      const req = store.get(tx.id);
      req.onsuccess = () => {
        // Deduplicate: skip if same id already queued
        if (req.result) {
          resolve();
          return;
        }
        try {
          const item = toQueueRecord(tx);
          if (household !== null) item[HOUSEHOLD_FIELD] = household;
          store.put(item);
        } catch (error) {
          reject(error);
        }
      };
    });
  } finally {
    db.close();
  }
}

async function runOfflineDrain(): Promise<DrainResult> {
  // Read token and household together so stale tabs cannot mix one household's token with another's records, consistent with api's localStorage token lookup.
  const { token, householdId: household } = authStore.getSession();
  const db = await openOfflineDB();
  const store: QueueStore = {
    getAll: () => new Promise((resolve, reject) => {
      const req = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAll();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    }),
    put: item => new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(item);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    }),
    delete: id => new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    }),
  };
  try {
    if (!token) {
      return {
        synced: 0, quarantinedForeign: 0, quarantinedInvalid: 0,
        remaining: splitQueue(await store.getAll(), household).pending.length, stoppedAt: 0,
      };
    }
    const post = (items: any[]) => {
      if (!sessionReload.writesAllowed()) {
        throw Object.assign(new Error('登入狀態已變更'), { code: 'SESSION_CHANGED' });
      }
      return fetch(`${API_BASE}/api/transactions/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ transactions: items }),
      });
    };
    const result = await drainQueue(store, post, household);
    if (result.synced > 0) invalidateListCache();
    return result;
  } finally {
    db.close();
  }
}

// Share one drain: one send sequence at a time keeps 100-row chunks ordered; the server ignores replays of rows it already has (workers/src/routes/transactions.ts POST /batch).
function drainOfflineQueue(): Promise<DrainResult> {
  if (!offlineQueueDrainPromise) {
    offlineQueueDrainPromise = runOfflineDrain().finally(() => { offlineQueueDrainPromise = null; });
  }
  return offlineQueueDrainPromise;
}

async function readQueueItems(): Promise<any[]> {
  const db = await openOfflineDB();
  try {
    const items = await new Promise<any[]>((resolve, reject) => {
      const req = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAll();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    return items;
  } finally {
    db.close();
  }
}

// A queued copy of a row the server already has must not outlive the row: when the user
// deletes the server row, the queued create would resurrect it at the next drain.
async function removeQueuedItem(id: string): Promise<void> {
  const db = await openOfflineDB();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

async function getOfflineQueueCount(): Promise<number> {
  try {
    return splitQueue(await readQueueItems(), currentHouseholdId()).pending.length;
  } catch { return 0; }
}

async function getUnsyncableItems(): Promise<any[]> {
  return splitQueue(await readQueueItems(), currentHouseholdId()).unsyncable;
}

async function getOtherHouseholdCount(): Promise<number> {
  return splitQueue(await readQueueItems(), currentHouseholdId()).otherHousehold.length;
}

// --- API helper ---
async function api(path: string, opts: RequestInit = {}): Promise<any> {
  const method = (opts.method || 'GET').toUpperCase();
  if (method !== 'GET' && !sessionReload.writesAllowed()) {
    throw Object.assign(new Error('登入狀態已變更'), { code: 'SESSION_CHANGED' });
  }
  // Pin the call-start token for every attempt, including retries, paired with the household read by the caller in the same synchronous turn.
  const token = authStore.getSession().token;

  try {
    const data = await apiRequest(path, opts, token, API_DEPS);
    if (method !== 'GET') invalidateListCache();
    return data;
  } catch (e: any) {
    // A 409 means the server holds a newer version than the cached list snapshot.
    if (e.code === 'CONFLICT') invalidateListCache();
    if (shouldClearForUpgrade(e, token, authStore.getSession().token)) {
      await authStore.clearToken();
      sessionReload.request('upgrade');
    }
    throw e;
  }
}

// --- Web Push helper ---
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

// --- Date helpers ---
function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function currentTime(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// --- Haptic feedback ---
function haptic(ms: number | number[]) {
  try { navigator.vibrate?.(ms); } catch {}
}

type QrCell = boolean | null;

const QR_L_BLOCKS: { total: number; data: number }[][] = [
  [],
  [{ total: 26, data: 19 }],
  [{ total: 44, data: 34 }],
  [{ total: 70, data: 55 }],
  [{ total: 100, data: 80 }],
  [{ total: 134, data: 108 }],
  [{ total: 86, data: 68 }, { total: 86, data: 68 }],
  [{ total: 98, data: 78 }, { total: 98, data: 78 }],
  [{ total: 121, data: 97 }, { total: 121, data: 97 }],
  [{ total: 146, data: 116 }, { total: 146, data: 116 }],
  [{ total: 86, data: 68 }, { total: 86, data: 68 }, { total: 87, data: 69 }, { total: 87, data: 69 }],
];

const QR_ALIGNMENT_POSITIONS: number[][] = [
  [],
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
];

const QR_EXP = new Array<number>(512);
const QR_LOG = new Array<number>(256);
let qrGfReady = false;

function initQrGf() {
  if (qrGfReady) return;
  let x = 1;
  for (let i = 0; i < 255; i++) {
    QR_EXP[i] = x;
    QR_LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) QR_EXP[i] = QR_EXP[i - 255];
  qrGfReady = true;
}

function qrGfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return QR_EXP[QR_LOG[a] + QR_LOG[b]];
}

function qrGeneratorPoly(degree: number): number[] {
  initQrGf();
  let poly = [1];
  for (let i = 0; i < degree; i++) {
    const next = new Array(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j++) {
      next[j] ^= poly[j];
      next[j + 1] ^= qrGfMul(poly[j], QR_EXP[i]);
    }
    poly = next;
  }
  return poly;
}

function qrRsEncode(data: number[], ecCount: number): number[] {
  const gen = qrGeneratorPoly(ecCount);
  const result = new Array(ecCount).fill(0);
  for (const b of data) {
    const factor = b ^ result[0];
    result.shift();
    result.push(0);
    for (let i = 0; i < ecCount; i++) {
      result[i] ^= qrGfMul(gen[i + 1], factor);
    }
  }
  return result;
}

function qrBchDigit(data: number): number {
  let digit = 0;
  while (data !== 0) {
    digit++;
    data >>>= 1;
  }
  return digit;
}

function qrBchTypeInfo(data: number): number {
  const g15 = 0x537;
  const g15Mask = 0x5412;
  let d = data << 10;
  while (qrBchDigit(d) - qrBchDigit(g15) >= 0) {
    d ^= g15 << (qrBchDigit(d) - qrBchDigit(g15));
  }
  return ((data << 10) | d) ^ g15Mask;
}

function qrBchTypeNumber(version: number): number {
  const g18 = 0x1f25;
  let d = version << 12;
  while (qrBchDigit(d) - qrBchDigit(g18) >= 0) {
    d ^= g18 << (qrBchDigit(d) - qrBchDigit(g18));
  }
  return (version << 12) | d;
}

function qrMask(mask: number, row: number, col: number): boolean {
  switch (mask) {
    case 0: return (row + col) % 2 === 0;
    case 1: return row % 2 === 0;
    case 2: return col % 3 === 0;
    case 3: return (row + col) % 3 === 0;
    case 4: return (Math.floor(row / 2) + Math.floor(col / 3)) % 2 === 0;
    case 5: return ((row * col) % 2) + ((row * col) % 3) === 0;
    case 6: return (((row * col) % 2) + ((row * col) % 3)) % 2 === 0;
    default: return (((row + col) % 2) + ((row * col) % 3)) % 2 === 0;
  }
}

function setupQrFinder(modules: QrCell[][], row: number, col: number) {
  const n = modules.length;
  for (let r = -1; r <= 7; r++) {
    for (let c = -1; c <= 7; c++) {
      const rr = row + r;
      const cc = col + c;
      if (rr < 0 || rr >= n || cc < 0 || cc >= n) continue;
      const dark = (r >= 0 && r <= 6 && (c === 0 || c === 6)) ||
        (c >= 0 && c <= 6 && (r === 0 || r === 6)) ||
        (r >= 2 && r <= 4 && c >= 2 && c <= 4);
      modules[rr][cc] = dark;
    }
  }
}

function setupQrAlignment(modules: QrCell[][], version: number) {
  const positions = QR_ALIGNMENT_POSITIONS[version];
  for (const row of positions) {
    for (const col of positions) {
      if (modules[row][col] !== null) continue;
      for (let r = -2; r <= 2; r++) {
        for (let c = -2; c <= 2; c++) {
          modules[row + r][col + c] = Math.max(Math.abs(r), Math.abs(c)) === 2 || (r === 0 && c === 0);
        }
      }
    }
  }
}

function setupQrTiming(modules: QrCell[][]) {
  const n = modules.length;
  for (let i = 8; i < n - 8; i++) {
    if (modules[i][6] === null) modules[i][6] = i % 2 === 0;
    if (modules[6][i] === null) modules[6][i] = i % 2 === 0;
  }
}

function setupQrTypeInfo(modules: QrCell[][], mask: number) {
  const n = modules.length;
  const bits = qrBchTypeInfo((1 << 3) | mask); // error correction level L
  for (let i = 0; i < 15; i++) {
    const dark = ((bits >>> i) & 1) === 1;
    if (i < 6) modules[i][8] = dark;
    else if (i < 8) modules[i + 1][8] = dark;
    else modules[n - 15 + i][8] = dark;

    if (i < 8) modules[8][n - i - 1] = dark;
    else if (i < 9) modules[8][15 - i] = dark;
    else modules[8][14 - i] = dark;
  }
  modules[n - 8][8] = true;
}

function setupQrVersionInfo(modules: QrCell[][], version: number) {
  if (version < 7) return;
  const n = modules.length;
  const bits = qrBchTypeNumber(version);
  for (let i = 0; i < 18; i++) {
    const dark = ((bits >>> i) & 1) === 1;
    modules[Math.floor(i / 3)][i % 3 + n - 11] = dark;
    modules[i % 3 + n - 11][Math.floor(i / 3)] = dark;
  }
}

function qrCreateDataCodewords(text: string): { version: number; data: number[] } {
  const bytes = Array.from(new TextEncoder().encode(text));
  let version = 0;
  let capacity = 0;
  for (let v = 1; v < QR_L_BLOCKS.length; v++) {
    capacity = QR_L_BLOCKS[v].reduce((sum, b) => sum + b.data, 0);
    const countBits = v < 10 ? 8 : 16;
    if (bytes.length < (1 << countBits) && 4 + countBits + bytes.length * 8 <= capacity * 8) {
      version = v;
      break;
    }
  }
  if (!version) throw new Error('QR data too long');

  capacity = QR_L_BLOCKS[version].reduce((sum, b) => sum + b.data, 0);
  const bits: number[] = [];
  const put = (value: number, length: number) => {
    for (let i = length - 1; i >= 0; i--) bits.push((value >>> i) & 1);
  };

  put(0b0100, 4); // byte mode
  put(bytes.length, version < 10 ? 8 : 16);
  for (const b of bytes) put(b, 8);

  const totalBits = capacity * 8;
  const terminator = Math.min(4, totalBits - bits.length);
  for (let i = 0; i < terminator; i++) bits.push(0);
  while (bits.length % 8 !== 0) bits.push(0);
  let padIndex = 0;
  while (bits.length < totalBits) {
    put(padIndex % 2 === 0 ? 0xec : 0x11, 8);
    padIndex++;
  }

  const data: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    data.push(bits.slice(i, i + 8).reduce((acc, bit) => (acc << 1) | bit, 0));
  }
  return { version, data };
}

function qrInterleaveBlocks(version: number, data: number[]): number[] {
  const blocks = QR_L_BLOCKS[version];
  let offset = 0;
  const dataBlocks: number[][] = [];
  const ecBlocks: number[][] = [];
  for (const b of blocks) {
    const chunk = data.slice(offset, offset + b.data);
    offset += b.data;
    dataBlocks.push(chunk);
    ecBlocks.push(qrRsEncode(chunk, b.total - b.data));
  }

  const result: number[] = [];
  const maxData = Math.max(...dataBlocks.map(b => b.length));
  for (let i = 0; i < maxData; i++) {
    for (const block of dataBlocks) {
      if (i < block.length) result.push(block[i]);
    }
  }
  const maxEc = Math.max(...ecBlocks.map(b => b.length));
  for (let i = 0; i < maxEc; i++) {
    for (const block of ecBlocks) {
      if (i < block.length) result.push(block[i]);
    }
  }
  return result;
}

function qrBuildMatrix(version: number, codewords: number[], mask: number): boolean[][] {
  const n = 21 + (version - 1) * 4;
  const modules: QrCell[][] = Array.from({ length: n }, () => Array<QrCell>(n).fill(null));
  setupQrFinder(modules, 0, 0);
  setupQrFinder(modules, n - 7, 0);
  setupQrFinder(modules, 0, n - 7);
  setupQrAlignment(modules, version);
  setupQrTiming(modules);
  setupQrTypeInfo(modules, mask);
  setupQrVersionInfo(modules, version);

  let row = n - 1;
  let direction = -1;
  let byteIndex = 0;
  let bitIndex = 7;

  for (let col = n - 1; col > 0; col -= 2) {
    if (col === 6) col--;
    while (true) {
      for (let c = 0; c < 2; c++) {
        const cc = col - c;
        if (modules[row][cc] === null) {
          let dark = false;
          if (byteIndex < codewords.length) dark = ((codewords[byteIndex] >>> bitIndex) & 1) === 1;
          if (qrMask(mask, row, cc)) dark = !dark;
          modules[row][cc] = dark;
          bitIndex--;
          if (bitIndex < 0) {
            byteIndex++;
            bitIndex = 7;
          }
        }
      }
      row += direction;
      if (row < 0 || row >= n) {
        row -= direction;
        direction = -direction;
        break;
      }
    }
  }

  return modules.map(r => r.map(v => !!v));
}

function qrLostPoint(modules: boolean[][]): number {
  const n = modules.length;
  let lost = 0;
  const countRun = (line: boolean[]) => {
    let runColor = line[0];
    let runLength = 1;
    for (let i = 1; i < line.length; i++) {
      if (line[i] === runColor) {
        runLength++;
      } else {
        if (runLength >= 5) lost += 3 + runLength - 5;
        runColor = line[i];
        runLength = 1;
      }
    }
    if (runLength >= 5) lost += 3 + runLength - 5;
  };
  for (let r = 0; r < n; r++) countRun(modules[r]);
  for (let c = 0; c < n; c++) countRun(modules.map(row => row[c]));

  for (let r = 0; r < n - 1; r++) {
    for (let c = 0; c < n - 1; c++) {
      const color = modules[r][c];
      if (modules[r + 1][c] === color && modules[r][c + 1] === color && modules[r + 1][c + 1] === color) lost += 3;
    }
  }

  const hasFinderLike = (line: boolean[], i: number) => {
    const pattern = [true, false, true, true, true, false, true];
    for (let j = 0; j < 7; j++) {
      if (line[i + j] !== pattern[j]) return false;
    }
    const before = i >= 4 && !line[i - 1] && !line[i - 2] && !line[i - 3] && !line[i - 4];
    const after = i + 11 <= line.length && !line[i + 7] && !line[i + 8] && !line[i + 9] && !line[i + 10];
    return before || after;
  };
  for (let r = 0; r < n; r++) {
    for (let i = 0; i <= n - 7; i++) if (hasFinderLike(modules[r], i)) lost += 40;
  }
  for (let c = 0; c < n; c++) {
    const line = modules.map(row => row[c]);
    for (let i = 0; i <= n - 7; i++) if (hasFinderLike(line, i)) lost += 40;
  }

  const dark = modules.flat().filter(Boolean).length;
  lost += Math.floor(Math.abs((dark * 100) / (n * n) - 50) / 5) * 10;
  return lost;
}

function makeQrMatrix(text: string): boolean[][] {
  const { version, data } = qrCreateDataCodewords(text);
  const codewords = qrInterleaveBlocks(version, data);
  let best = qrBuildMatrix(version, codewords, 0);
  let bestScore = qrLostPoint(best);
  for (let mask = 1; mask < 8; mask++) {
    const candidate = qrBuildMatrix(version, codewords, mask);
    const score = qrLostPoint(candidate);
    if (score < bestScore) {
      best = candidate;
      bestScore = score;
    }
  }
  return best;
}

function drawQrToCanvas(canvas: HTMLCanvasElement, text: string) {
  const modules = makeQrMatrix(text);
  const quiet = 4;
  const cssSize = 192;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = cssSize * dpr;
  canvas.height = cssSize * dpr;
  canvas.style.width = `${cssSize}px`;
  canvas.style.height = `${cssSize}px`;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const moduleCount = modules.length + quiet * 2;
  const cell = Math.floor(canvas.width / moduleCount);
  const offset = Math.floor((canvas.width - cell * modules.length) / 2);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#111827';
  for (let r = 0; r < modules.length; r++) {
    for (let c = 0; c < modules.length; c++) {
      if (modules[r][c]) ctx.fillRect(offset + c * cell, offset + r * cell, cell, cell);
    }
  }
}

// --- Category icon definitions (Heroicon outline 24x24 SVG paths) ---
const CATEGORY_ICONS: Record<string, { path: string; color: string }> = {
  'cake':           { path: 'M12 8.25v-1.5m0 1.5c-1.355 0-2.697.056-4.024.166C6.845 8.51 6 9.473 6 10.608v2.513m6-4.871c1.355 0 2.697.056 4.024.166C17.155 8.51 18 9.473 18 10.608v2.513M15 8.25v-1.5m-6 1.5v-1.5m12 9.75-1.5.75a3.354 3.354 0 0 1-3 0 3.354 3.354 0 0 0-3 0 3.354 3.354 0 0 1-3 0 3.354 3.354 0 0 0-3 0 3.354 3.354 0 0 1-3 0L3 16.5m15-3.379a48.474 48.474 0 0 0-6-.371c-2.032 0-4.034.126-6 .371m12 0c.39.049.777.102 1.163.16 1.07.16 1.837 1.094 1.837 2.175v5.169c0 .621-.504 1.125-1.125 1.125H4.125A1.125 1.125 0 0 1 3 20.625v-5.17c0-1.08.768-2.014 1.837-2.174A47.78 47.78 0 0 1 6 13.12M12.265 3.11a.375.375 0 1 1-.53 0L12 2.845l.265.265Zm-3 0a.375.375 0 1 1-.53 0L9 2.845l.265.265Zm6 0a.375.375 0 1 1-.53 0L15 2.845l.265.265Z', color: '#F43F5E' },
  'home':           { path: 'm2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25', color: '#0D9488' },
  'truck':          { path: 'M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12', color: '#3B82F6' },
  'scissors':       { path: 'm7.848 8.25 1.536.887M7.848 8.25a3 3 0 1 1-5.196-3 3 3 0 0 1 5.196 3Zm1.536.887a2.165 2.165 0 0 1 1.083 1.839c.005.351.054.695.14 1.024M9.384 9.137l2.077 1.199M7.848 15.75l1.536-.887m-1.536.887a3 3 0 1 1-5.196 3 3 3 0 0 1 5.196-3Zm1.536-.887a2.165 2.165 0 0 0 1.083-1.838c.005-.352.054-.695.14-1.025m-1.223 2.863 2.077-1.199m0-3.328a4.323 4.323 0 0 1 2.068-1.379l5.325-1.628a4.5 4.5 0 0 1 2.48-.044l.803.215-7.794 4.5m-2.882-1.664A4.33 4.33 0 0 0 10.607 12m3.736 0 7.794 4.5-.802.215a4.5 4.5 0 0 1-2.48-.043l-5.326-1.629a4.324 4.324 0 0 1-2.068-1.379M14.343 12l-2.882 1.664', color: '#8B5CF6' },
  'book-open':      { path: 'M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25', color: '#F59E0B' },
  'heart':          { path: 'M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z', color: '#EF4444' },
  'sparkles':       { path: 'M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z', color: '#EC4899' },
  'gift':           { path: 'M21 11.25v8.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 1 0 9.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1 1 14.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z', color: '#6B7280' },
  'banknotes':      { path: 'M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z', color: '#10B981' },
  'chart-bar':      { path: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z', color: '#10B981' },
  'building-office': { path: 'M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21', color: '#10B981' },
  'wallet':         { path: 'M21 12a2.25 2.25 0 0 0-2.25-2.25H15a3 3 0 1 1-6 0H5.25A2.25 2.25 0 0 0 3 12m18 0v6a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 9m18 0V6a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6v3', color: '#10B981' },
  // Extra icons for icon picker
  'shopping-bag':   { path: 'M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z', color: '#F43F5E' },
  'phone':          { path: 'M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z', color: '#3B82F6' },
  'wifi':           { path: 'M8.288 15.038a5.25 5.25 0 0 1 7.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 0 1 1.06 0Z', color: '#0D9488' },
  'fire':           { path: 'M15.362 5.214A8.252 8.252 0 0 1 12 21 8.25 8.25 0 0 1 6.038 7.047 8.287 8.287 0 0 0 9 9.601a8.983 8.983 0 0 1 3.361-6.867 8.21 8.21 0 0 0 3 2.48Z M12 18a3.75 3.75 0 0 0 .495-7.468 5.99 5.99 0 0 0-1.925 3.547 5.975 5.975 0 0 1-2.133-1.001A3.75 3.75 0 0 0 12 18Z', color: '#EF4444' },
  'globe-alt':      { path: 'M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418', color: '#0D9488' },
  'musical-note':   { path: 'm9 9 10.5-3m0 6.553v3.75a2.25 2.25 0 0 1-1.632 2.163l-1.32.377a1.803 1.803 0 1 1-.99-3.467l2.31-.66a2.25 2.25 0 0 0 1.632-2.163Zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 0 1-1.632 2.163l-1.32.377a1.803 1.803 0 0 1-.99-3.467l2.31-.66A2.25 2.25 0 0 0 9 15.553Z', color: '#EC4899' },
  'paint-brush':    { path: 'M9.53 16.122a3 3 0 0 0-5.78 1.128 2.25 2.25 0 0 1-2.4 2.245 4.5 4.5 0 0 0 8.4-2.245c0-.399-.078-.78-.22-1.128Zm0 0a15.998 15.998 0 0 0 3.388-1.62m-5.043-.025a15.994 15.994 0 0 1 1.622-3.395m3.42 3.42a15.995 15.995 0 0 0 4.764-4.648l3.876-5.814a1.151 1.151 0 0 0-1.597-1.597L14.146 6.32a15.996 15.996 0 0 0-4.649 4.763m3.42 3.42a6.776 6.776 0 0 0-3.42-3.42', color: '#8B5CF6' },
  'academic-cap':   { path: 'M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5', color: '#F59E0B' },
  'briefcase':      { path: 'M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 0 0 .75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 0 0-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0 1 12 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 0 1-.673-.38m0 0A2.18 2.18 0 0 1 3 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 0 1 3.413-.387m7.5 0V5.25A2.25 2.25 0 0 0 13.5 3h-3a2.25 2.25 0 0 0-2.25 2.25v.894m7.5 0a48.667 48.667 0 0 0-7.5 0M12 12.75h.008v.008H12v-.008Z', color: '#6B7280' },
  'clock':          { path: 'M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z', color: '#6B7280' },
  'tag':            { path: 'M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z M6 6h.008v.008H6V6Z', color: '#0D9488' },
};

// Available icon IDs for icon picker (subset for UI)
const AVAILABLE_ICONS = [
  'cake', 'home', 'truck', 'scissors', 'book-open', 'heart', 'sparkles', 'gift',
  'banknotes', 'chart-bar', 'building-office', 'wallet', 'shopping-bag', 'phone',
  'wifi', 'fire', 'globe-alt', 'musical-note', 'paint-brush', 'academic-cap',
  'briefcase', 'clock', 'tag',
];

// Kiddo avatar picker options (R2 onboarding) — mirrors AVATAR_EMOJI in workers/src/routes/kiddo.ts
const KIDDO_AVATAR_OPTIONS = [
  { slug: 'lion', emoji: '🦁' }, { slug: 'tiger', emoji: '🐯' }, { slug: 'bear', emoji: '🐻' },
  { slug: 'fox', emoji: '🦊' }, { slug: 'wolf', emoji: '🐺' }, { slug: 'dragon', emoji: '🐉' },
  { slug: 'cat', emoji: '🐱' }, { slug: 'dog', emoji: '🐶' }, { slug: 'rabbit', emoji: '🐰' },
  { slug: 'panda', emoji: '🐼' }, { slug: 'owl', emoji: '🦉' }, { slug: 'eagle', emoji: '🦅' },
  { slug: 'monkey', emoji: '🐵' }, { slug: 'koala', emoji: '🐨' },
];

// --- Smart defaults: time-based category ---
function getSmartCategoryId(categories: any[]): string {
  const h = new Date().getHours();
  const isMealTime = (h >= 6 && h <= 9) || (h >= 11 && h < 14) || (h >= 17 && h <= 20);
  if (isMealTime) {
    const food = categories.find((c: any) => c.type === 'expense' && (c.name.includes('飲食') || c.name.includes('餐')));
    if (food) return food.id;
  }
  // Fallback: last used or first expense category
  const lastCat = localStorage.getItem('bk_last_category');
  if (lastCat && categories.find((c: any) => c.id === lastCat)) return lastCat;
  const firstExpense = categories.find((c: any) => c.type === 'expense');
  return firstExpense?.id || '';
}

// --- Alpine.js App ---
Alpine.data('bookkeeper', () => ({
    // Auth
    authed: false,
    needSetup: false,
    pin: '',
    pinError: '',
    token: '',
    isSubmitting: false,
    sessionChangeBlocked: false,

    // Onboarding (multi-tenant)
    onboardingStep: '' as '' | 'choose' | 'create' | 'join',
    createForm: { name: '', pin: '', member1: '', member2: '' },
    joinForm: { invite_code: '', pin: '', previewName: '', previewError: '' },

    // Household
    householdName: '',
    editingHouseholdName: false,
    householdNameDraft: '',
    invites: [] as { id: string; code: string; expires_at: string; used_at: string | null; used_by: string | null; created_at: string }[],
    members: [] as { id: string; slug: string; name: string; color: string; avatar_label: string; is_active: number; is_kid?: number }[],

    // Member management
    showAddMember: false,
    showEditMember: false,
    addMemberForm: { name: '', slug: '', color: '#6B7280' },
    editMemberForm: { id: '', name: '', color: '', avatar_label: '' },

    // Navigation
    tab: 'home',
    showAddSheet: false,
    showEditSheet: false,
    showNoteDate: false,
    showKiddoCategories: false,
    showEditKiddoCategories: false,

    // Kiddo Parent UI R1 — attention entry point (DEV_SPEC_KIDDO_PARENT_UI.md Rev 1.0)
    showKiddoPanel: false,
    kiddoLoading: false,
    kiddoLoaded: false,
    kiddoKids: [] as {
      id: string; member_slug: string; display_name: string; avatar: string; avatar_emoji: string;
      level: number; xp: number; current_streak: number; last_logged_date: string | null;
      pending_weeks: string[]; last_review_week: string | null; last_review_at: string | null;
      days_since_last_review: number | null; stale_review: boolean;
    }[],
    kiddoLookbackWeeks: 8,
    // Fallback only — authoritative value synced from the API's review_stale_days
    // (source of truth: workers/src/routes/kiddo.ts REVIEW_STALE_DAYS).
    kiddoReviewStaleDays: 8,

    // Kids the parent explicitly disabled (P1 fix — GET /profiles-disabled). Distinct
    // from kiddoKids (active only): without this list, disabling a kid was a UI dead
    // end because the reactivate button only lives on the dashboard, and the dashboard
    // is only reachable by tapping a row in kiddoKids (which excludes revoked kids).
    kiddoDisabled: [] as {
      id: string; member_slug: string; display_name: string; avatar: string;
      disabled_at: string; token_created_at: string | null; token_last_used_at: string | null;
    }[],
    kiddoDisabledExpanded: false,

    // Kiddo Parent UI R2 — onboarding wizard (DEV_SPEC_KIDDO_PARENT_UI.md Rev 1.0 §R2)
    kiddoAvatarOptions: KIDDO_AVATAR_OPTIONS,
    kiddoOnboardStep: '' as '' | 'member' | 'balance' | 'allowance' | 'share' | 'activate',
    kiddoOnboardForm: {
      member_slug: '', display_name: '', avatar: 'bear',
      opening_cash: '' as string | number, opening_easycard: '' as string | number,
      as_of_date: today(),
    },
    kiddoOnboardShowNewMember: false,
    kiddoOnboardNewMemberForm: { name: '' },
    kiddoOnboardSaving: false,
    kiddoOnboardError: '',
    kiddoOnboardResult: null as { profile_id: string; member_slug: string; display_name: string; api_token: string } | null,
    kiddoOnboardShareConfirmed: false,
    kiddoOnboardCheckingActivation: false,
    // GET /api/kiddo-manage/profiles — includes token_last_used_at for the activation gauge
    // (attention endpoint's kids[] does not carry token fields, R1 contract is frozen).
    kiddoProfiles: [] as {
      id: string; member_slug: string; display_name?: string;
      token_created_at: string | null; token_last_used_at: string | null; token_revoked_at?: string | null;
    }[],
    kiddoShareModalOpen: false,
    kiddoShareConfirmed: false,
    kiddoSharePayload: null as { profile_id: string; member_slug: string; display_name: string; api_token: string; source: 'onboard' | 'rotate' } | null,
    kiddoTokenConfirmOpen: false,
    kiddoTokenConfirmAction: 'rotate' as 'rotate' | 'reactivate',
    kiddoTokenActionLoading: false,
    kiddoTokenActionError: '',

    // Kiddo Parent UI R8 — per-kid dashboard (tap a kid row in the list view)
    kiddoView: 'list' as 'list' | 'dashboard' | 'review',
    kiddoDashboardSlug: '',
    kiddoDashboardLoading: false,
    kiddoDashboardError: false,
    kiddoDashboard: null as any,
    kiddoAllowances: [] as any[],
    kiddoAllowanceLoading: false,
    kiddoAllowanceError: '',
    kiddoAllowanceSaving: false,
    kiddoAllowanceEditingId: '',
    kiddoAllowanceForm: {
      amount: '' as string | number,
      frequency: 'weekly',
      day_of_week: 1 as string | number,
      day_of_month: 1 as string | number,
      next_due: today(),
    },
    kiddoManualSaving: false,
    kiddoManualError: '',
    kiddoManualForm: {
      direction: 'give',
      wallet: 'cash',
      amount: '' as string | number,
      note: '',
      client_tx_id: '',
    },
    kiddoReconcileForm: {
      actual_cash: '' as string | number,
      actual_easycard: '' as string | number,
      reason: '',
      client_tx_id: '',
    },
    kiddoReconcileSaving: false,
    kiddoReconcileError: '',
    kiddoReconcileReverseId: '',

    // Kiddo Parent UI R6 — weekly review detail + approval (Q6: approve-only, no
    // reject/return-for-correction loop — parents edit mis-logged tx in place instead)
    kiddoReviewWeek: '',
    kiddoReviewLoading: false,
    kiddoReviewError: false,
    kiddoReviewDetail: null as any,
    kiddoReviewNote: '',
    kiddoReviewApproving: false,
    kiddoReviewApproveError: '',

    // Note autocomplete
    noteHistory: JSON.parse(localStorage.getItem('bk_note_history') || '[]') as string[],
    noteSuggestions: [] as string[],
    showNoteSuggestions: false,

    // Toast
    toast: { show: false, msg: '', type: 'success' },
    toastTimer: undefined as ReturnType<typeof setTimeout> | undefined,

    // Categories, subcategories, tags (loaded once)
    allCategories: [] as any[],
    allSubcategories: [] as any[],
    tags: [] as any[],

    // Add form
    form: {
      type: 'expense',
      amount: '',
      currency: localStorage.getItem('bk_currency') || 'TWD',
      category_id: '',
      subcategory_id: '',
      description: '',
      date: today(),
      created_by: 'shared',
      tags: [] as string[],
      payment_method_id: '',
      property_tag: '',
    },

    // Edit form
    editForm: {
      id: '', type: 'expense', amount: '' as string | number, currency: 'TWD',
      category_id: '', subcategory_id: '', description: '',
      date: '', created_by: 'shared', updated_at: '',
      payment_method_id: '',
      property_tag: '',
    },

    // Property tag dropdown options (kept in sync with workers/src/properties.ts)
    propertyOptions: [
      { tag: '',               displayName: '（無）' },
      { tag: 'Garden2A1',      displayName: '花園流域 2A1' },
      { tag: 'Garden2A2',      displayName: '花園流域 2A2' },
      { tag: 'GardenShared',   displayName: '花園流域 共用' },
      { tag: 'TinyEra',        displayName: '小時代' },
      { tag: 'QuanyouTianchi', displayName: '全友天池' },
      { tag: 'Phoenix',        displayName: '鳳凰城' },
    ],

    // Conflict modal
    showConflictModal: false,

    // List tab
    listMonth: currentMonth(),
    transactions: [] as any[],
    pendingInList: 0,
    listLoadSeq: 0,
    searchQuery: '',
    listSummary: { income: 0, expense: 0, net: 0 },
    memberFilter: '',
    propertyFilter: '',
    showCalendar: true,
    selectedCalendarDay: '',
    todayStr: today(),

    // Dashboard
    dashboard: {
      todayExpense: 0,
      todayUsd: 0,
      monthExpense: 0,
      monthIncome: 0,
      monthNet: 0,
      monthExpenseUsd: 0,
      monthIncomeUsd: 0,
      recent: [] as any[],
      topCategories: [] as any[],
      has_fx: false,
    },
    dashLoading: false,
    dashLoadSeq: 0,
    dashChartInstance: null as any,

    // Payment methods
    paymentMethods: [] as any[],
    showAddPaymentMethod: false,
    editPaymentMethodId: '',
    addPaymentMethodForm: { name: '', type: 'credit_card', member_slug: '' },
    showPaymentDropdown: false,
    showEditPaymentDropdown: false,

    // Report tab
    reportType: 'monthly',
    reportMonth: currentMonth(),
    reportYear: new Date().getFullYear(),
    reportData: {} as any,
    chartInstance: null as any,
    chartToggle: 'expense' as 'expense' | 'income',
    drillCategory: '' as string,
    drillTransactions: [] as any[],
    drillLoading: false,
    drillMember: '' as string,
    drillMemberCat: '' as string,
    memberDrillTxs: [] as any[],
    memberDrillLoading: false,
    reportPaymentFilter: '',
    quickNote: { show: false, tx: null as any, text: '' },
    addNote: { show: false, text: '', suggestions: [] as string[] },

    // Settings modals
    showAddCategoryModal: false,
    showEditCategoryModal: false,
    showAddSubcategoryModal: false,
    showEditSubcategoryModal: false,
    showAddTag: false,
    showChangePin: false,
    showDiagnostics: false,
    expandedCategory: '',

    // Recurring rules
    recurringRules: [] as any[],
    expandedRecurringCat: '' as string,
    showAddRecurring: false,
    showEditRecurring: false,
    recurringFromAddSheet: false,
    recurringForm: {
      id: '', type: 'expense' as string, amount: '', currency: 'TWD',
      category_id: '', subcategory_id: '', description: '',
      created_by: 'shared', day_of_month: 1, start_date: today(),
      end_date: '', remaining_count: '' as string | number, end_mode: 'count' as string,
      skip_short_month: 0, payment_method_id: '',
    },
    showRecurringPaymentDropdown: false,

    availableIcons: AVAILABLE_ICONS,
    addCategoryForm: { icon: '', name: '', type: 'expense' },
    editCategoryForm: { id: '', icon: '', name: '', type: 'expense' },
    addSubcategoryForm: { category_id: '', name: '', parentIcon: '', parentName: '' },
    editSubcategoryForm: { id: '', name: '', parentName: '' },
    addTagForm: { name: '', color: '#0D9488' },
    changePinForm: { old_pin: '', new_pin: '', error: '' },

    // Import
    importProgress: '' as string,

    // Push notifications
    pushSupported: false,
    pushEnabled: false,
    offlineStartup: false,
    formUsable: true,
    offlineStartupRecoveryPromise: null as Promise<void> | null,

    // Diagnostics
    apiBase: API_BASE,
    appVersion: __APP_VERSION__,
    buildSha: __BUILD_SHA__,
    diagOfflineCount: 0,
    diagUnsyncableCount: 0,
    diagOtherHouseholdCount: 0,
    diagUnsyncableDeleteIds: [] as string[],
    diagQueueBusy: false,
    coldStartSyncPending: false,
    syncRerunPending: false,

    // --- Computed ---
    get filteredCategories() {
      return this.allCategories.filter((c: any) => c.type === this.form.type);
    },
    get mainCategories() {
      return this.allCategories.filter((c: any) => c.type === this.form.type && !c.id.startsWith('kid_'));
    },
    get kiddoCategories() {
      return this.allCategories.filter((c: any) => c.type === this.form.type && c.id.startsWith('kid_'));
    },

    // Kiddo Parent UI R1 — attention summary (home card + settings row gating)
    get kiddoPendingWeeksTotal() {
      return this.kiddoKids.reduce((sum: number, k: any) => sum + k.pending_weeks.length, 0);
    },
    get kiddoStaleKids() {
      return this.kiddoKids.filter((k: any) => k.pending_weeks.length === 0 && k.stale_review);
    },
    get kiddoHasAttention() {
      return this.kiddoPendingWeeksTotal > 0 || this.kiddoStaleKids.length > 0;
    },
    get kiddoAttentionSummary() {
      const pendingTotal = this.kiddoPendingWeeksTotal;
      if (pendingTotal > 0) {
        const kidsWithPending = this.kiddoKids.filter((k: any) => k.pending_weeks.length > 0);
        if (kidsWithPending.length === 1) {
          return `${kidsWithPending[0].display_name} ${kidsWithPending[0].pending_weeks.length} 週記帳待審核`;
        }
        return `共 ${pendingTotal} 週記帳待審核`;
      }
      if (this.kiddoStaleKids.length > 0) {
        return `已超過 ${this.kiddoReviewStaleDays} 天未審核小孩記帳`;
      }
      return '';
    },

    // Kiddo Parent UI R6 — pending weeks for the kid currently open in the dashboard.
    // Sourced from kiddoKids (loaded by loadKiddoAttention), not re-fetched, so it
    // stays in sync with the home/list badge without an extra round trip.
    get kiddoDashboardPendingWeeks() {
      const k = this.kiddoKids.find((x: any) => x.member_slug === this.kiddoDashboardSlug);
      return k ? k.pending_weeks : [];
    },
    get kiddoDashboardProfileId() {
      const fromList = this.kiddoKids.find((x: any) => x.member_slug === this.kiddoDashboardSlug)?.id;
      const fromProfiles = this.kiddoProfiles.find((x: any) => x.member_slug === this.kiddoDashboardSlug)?.id;
      return fromList || fromProfiles || this.kiddoDashboard?.profile?.id || '';
    },
    get kiddoDashboardTokenRevoked() {
      return !!(this.kiddoDashboard?.gauge?.token_revoked || this.kiddoDashboard?.profile?.token_revoked_at);
    },
    get kiddoDashboardTokenAgeWarning() {
      const age = this.kiddoDashboard?.gauge?.token_age_days;
      return typeof age === 'number' && age > 90;
    },
    get kiddoReconcileActualCashValue() {
      const raw = this.kiddoReconcileForm.actual_cash;
      if (raw === '' || raw === null || raw === undefined) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? Math.round(n) : null;
    },
    get kiddoReconcileActualEasycardValue() {
      const raw = this.kiddoReconcileForm.actual_easycard;
      if (raw === '' || raw === null || raw === undefined) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? Math.round(n) : null;
    },
    get kiddoReconcileCashDiff() {
      if (!this.kiddoDashboard || this.kiddoReconcileActualCashValue === null) return null;
      return this.kiddoReconcileActualCashValue - Math.round(this.kiddoDashboard.balances.cash || 0);
    },
    get kiddoReconcileEasycardDiff() {
      if (!this.kiddoDashboard || this.kiddoReconcileActualEasycardValue === null) return null;
      return this.kiddoReconcileActualEasycardValue - Math.round(this.kiddoDashboard.balances.easycard || 0);
    },
    get kiddoReconcileCanSubmit() {
      return !this.kiddoReconcileSaving &&
        this.kiddoReconcileActualCashValue !== null &&
        this.kiddoReconcileActualEasycardValue !== null &&
        this.kiddoReconcileActualCashValue >= 0 &&
        this.kiddoReconcileActualEasycardValue >= 0 &&
        this.kiddoReconcileForm.reason.trim().length > 0;
    },
    get kiddoShareUrl() {
      if (!this.kiddoSharePayload) return '';
      return this.kiddoLoginUrl(this.kiddoSharePayload.member_slug, this.kiddoSharePayload.api_token);
    },
    get kiddoDashboardAllowances() {
      const profileId = this.kiddoDashboardProfileId;
      if (!profileId) return [];
      return this.kiddoAllowances.filter((a: any) => a.profile_id === profileId);
    },
    get kiddoActiveAllowance() {
      return this.kiddoDashboardAllowances.find((a: any) => a.is_active === 1 && !a.ended_at) || null;
    },
    get kiddoAllowancePreview() {
      const amount = Number(this.kiddoAllowanceForm.amount) || 0;
      const prefix = this.kiddoAllowanceForm.frequency === 'monthly'
        ? `每月 ${this.kiddoAllowanceForm.day_of_month || 1} 日`
        : (this.kiddoAllowanceForm.frequency === 'biweekly'
          ? `每兩週週${this.kiddoWeekdayLabel(Number(this.kiddoAllowanceForm.day_of_week) || 0)}`
          : `每週週${this.kiddoWeekdayLabel(Number(this.kiddoAllowanceForm.day_of_week) || 0)}`);
      const start = this.kiddoAllowanceForm.next_due || '未選日期';
      return `${prefix} +$${amount.toLocaleString()}，${start} 起`;
    },

    // Kiddo Parent UI R2 — onboarding wizard computed
    get kiddoOnboardExistingSlugs() {
      // Household members that already have a kiddo profile — used to grey out /
      // block re-selection in the onboarding member-picker step (DUPLICATE 409 avoided
      // at the UI layer, not just relied on as a server-side 409).
      return new Set(this.kiddoKids.map((k: any) => k.member_slug));
    },
    get kiddoOnboardActivated() {
      // Onboarding "complete" = the created profile's token has been used at least
      // once (token generation != activation, R2 completion condition).
      if (!this.kiddoOnboardResult) return false;
      const p = this.kiddoProfiles.find((x: any) => x.id === this.kiddoOnboardResult!.profile_id);
      return !!(p && (p as any).token_last_used_at);
    },

    get selectedCategorySubs() {
      if (!this.form.category_id) return [];
      const cat = this.allCategories.find((c: any) => c.id === this.form.category_id);
      return cat?.subcategories || [];
    },

    get canSave() {
      if (this.sessionChangeBlocked) return false;
      const amount = this.evalCalcExpr(this.form.amount);
      const storedAmount = this.toStoredAmount(amount, this.form.currency);
      return Boolean(this.form.amount && Number.isFinite(amount) && storedAmount > 0 && this.form.category_id);
    },

    get canSaveEdit() {
      const amount = this.evalCalcExpr(String(this.editForm.amount));
      const storedAmount = this.toStoredAmount(amount, this.editForm.currency);
      return Boolean(this.editForm.amount && Number.isFinite(amount) && storedAmount > 0 && this.editForm.category_id);
    },

    get groupedTransactions() {
      const groups: { date: string; items: any[] }[] = [];
      let currentDate = '';
      for (const tx of this.transactions) {
        if (tx.date !== currentDate) {
          currentDate = tx.date;
          groups.push({ date: tx.date, items: [] });
        }
        groups[groups.length - 1].items.push(tx);
      }
      return groups;
    },

    get filteredGroupedTransactions() {
      if (!this.selectedCalendarDay) return this.groupedTransactions;
      const txs = this.transactions.filter((tx: any) => tx.date === this.selectedCalendarDay);
      if (txs.length === 0) return [];
      return [{ date: this.selectedCalendarDay, items: txs }];
    },

    get calendarGrid() {
      const [y, m] = this.listMonth.split('-').map(Number);
      const firstDay = new Date(y, m - 1, 1);
      const lastDay = new Date(y, m, 0);
      const startDow = (firstDay.getDay() + 6) % 7; // Monday = 0

      const dailyTotals: Record<string, { expense: number; income: number }> = {};
      for (const tx of this.transactions) {
        if (tx._pending) continue;
        if (!dailyTotals[tx.date]) dailyTotals[tx.date] = { expense: 0, income: 0 };
        dailyTotals[tx.date][tx.type as 'expense' | 'income'] += tx.amount;
      }

      const weeks: any[][] = [];
      let week: any[] = [];
      for (let i = 0; i < startDow; i++) week.push(null);

      for (let d = 1; d <= lastDay.getDate(); d++) {
        const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        const t = dailyTotals[dateStr] || { expense: 0, income: 0 };
        week.push({ day: d, dateStr, expense: t.expense, income: t.income });
        if (week.length === 7) { weeks.push(week); week = []; }
      }
      while (week.length > 0 && week.length < 7) week.push(null);
      if (week.length) weeks.push(week);
      return weeks;
    },

    get activePaymentMethods() {
      return this.paymentMethods.filter((pm: any) => pm.is_active !== 0);
    },

    // Payment methods: ANY member may select ANY active card — including another
    // member's personal card (e.g. fuel paid on a spouse's card). The former
    // per-member restriction (adult → own + shared cards only) was removed per user
    // request 2026-05-31; payment_method is independent of created_by (backend already
    // accepts any card↔member combination). To restore own-first UX later, sort here.
    get formPaymentMethods() {
      return this.paymentMethods.filter((pm: any) => pm.is_active !== 0);
    },

    get editFormPaymentMethods() {
      return this.paymentMethods.filter((pm: any) => pm.is_active !== 0);
    },

    get recurringFormPaymentMethods() {
      return this.paymentMethods.filter((pm: any) => pm.is_active !== 0);
    },

    get expenseCategories() {
      if (!this.reportData.byCategory) return [];
      return this.reportData.byCategory.filter((c: any) => c.type === 'expense');
    },

    get incomeCategories() {
      if (!this.reportData.byCategory) return [];
      return this.reportData.byCategory.filter((c: any) => c.type === 'income');
    },

    get memberExpenseReport() {
      if (!this.reportData.byMember) return [];
      return this.reportData.byMember.filter((m: any) => m.type === 'expense');
    },

    get memberIncomeReport() {
      if (!this.reportData.byMember) return [];
      return this.reportData.byMember.filter((m: any) => m.type === 'income');
    },

    get groupedRecurringRules() {
      const groups: Record<string, { category_id: string; category_name: string; category_icon: string; type: string; total: number; rules: any[] }> = {};
      for (const rule of this.recurringRules) {
        if (!rule.is_active) continue;
        const key = rule.category_id;
        if (!groups[key]) {
          groups[key] = { category_id: key, category_name: rule.category_name, category_icon: rule.category_icon, type: rule.type, total: 0, rules: [] };
        }
        groups[key].rules.push(rule);
        groups[key].total += this.normAmount(rule); // cents currencies stored ×100

      }
      // Sort: expense first, then by total descending
      return Object.values(groups).sort((a, b) => {
        if (a.type !== b.type) return a.type === 'expense' ? -1 : 1;
        return b.total - a.total;
      });
    },

    // --- Property tag helpers ---
    get showPropertyDropdown(): boolean {
      return !(this.form.type === 'income' && this.form.category_id === 'income_rent');
    },
    get showEditPropertyDropdown(): boolean {
      return !(this.editForm.type === 'income' && this.editForm.category_id === 'income_rent');
    },
    getPropertyIconSvg(size: string = 'w-3 h-3'): string {
      const path = CATEGORY_ICONS['building-office']?.path || '';
      return `<svg class="${size}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="${path}" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/></svg>`;
    },
    getPropertyShortLabel(tag: string | null | undefined): string {
      if (!tag) return '';
      const opt = this.propertyOptions.find((p: any) => p.tag === tag);
      // displayName is e.g. "花園流域 2A1" → return last segment for compactness
      if (!opt) return tag;
      const parts = opt.displayName.split(' ');
      return parts[parts.length - 1];
    },

    // --- Member helpers (dynamic from API) ---
    memberColorHex(slug: string): string {
      return this.members.find((m: any) => m.slug === slug)?.color || '#6B7280';
    },
    memberLabel(member: string): string {
      const m = this.members.find((m: any) => m.slug === member);
      return m?.avatar_label || m?.name?.[0] || member[0] || '?';
    },
    memberDisplayName(member: string): string {
      return this.members.find((m: any) => m.slug === member)?.name || member;
    },
    get activeMembers() {
      return this.members.filter((m: any) => m.is_active);
    },

    // --- Currency helpers ---
    compactAmount(n: number): string {
      const abs = Math.abs(n);
      if (abs >= 100_000_000) return (n / 100_000_000).toFixed(1) + '億';
      if (abs >= 10_000) return Math.round(n / 10_000).toLocaleString() + '萬';
      return n.toLocaleString();
    },
    // USD/EUR are stored in cents (x100, decimal keypad). JPY is whole-unit like TWD but foreign.
    isCents(currency: string): boolean {
      return currency === 'USD' || currency === 'EUR';
    },
    toStoredAmount(amount: number, currency: string): number {
      return this.isCents(currency) ? Math.round(amount * 100) : Math.round(amount);
    },
    nextCurrency(currency: string): string {
      const order = ['TWD', 'USD', 'EUR', 'JPY'];
      return order[(order.indexOf(currency) + 1) % order.length];
    },
    currencySymbol(currency: string): string {
      return currency === 'USD' ? 'US$' : currency === 'EUR' ? '€' : currency === 'JPY' ? '¥' : '$';
    },
    normAmount(tx: { amount: number; currency?: string }): number {
      return this.isCents(tx.currency || '') ? tx.amount / 100 : tx.amount;
    },
    formatAmount(amount: string | number, currency: string): string {
      const num = typeof amount === 'string' ? parseInt(amount) || 0 : amount;
      if (this.isCents(currency)) {
        return (num / 100).toFixed(2);
      }
      return num.toLocaleString();
    },
    // Format amount during keypad entry (USD/EUR: user types units.cents, TWD: whole numbers)
    formatEntryAmount(amount: string, currency: string): string {
      // If expression has operators, format each segment individually
      if (/\d[+\-×÷]/.test(amount)) {
        return amount.replace(/(\d+\.?\d*)/g, (seg) => {
          if (this.isCents(currency)) {
            if (seg.includes('.')) return seg; // preserve typing state
            return parseFloat(seg).toFixed(2);
          }
          return parseInt(seg).toLocaleString();
        });
      }
      if (this.isCents(currency)) {
        if (!amount) return '0.00';
        if (amount.includes('.')) return amount; // preserve typing state: "7.", "7.1", "7.18"
        const num = parseInt(amount) || 0;
        return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      const num = parseInt(amount) || 0;
      return num.toLocaleString();
    },
    // Font size for the big amount display: symbol + formatted amount total glyphs decide the tier.
    // Fixes overflow: old inline ternaries counted digits only, so 'US$' (3 glyphs) blew the box on long decimals.
    amountFontClass(amount: string | number, currency: string): string {
      const len = this.currencySymbol(currency).length + this.formatEntryAmount(String(amount ?? '') || '0', currency).length;
      if (len <= 7) return 'text-[4.5rem]';
      if (len <= 9) return 'text-[3.5rem]';
      if (len <= 12) return 'text-[2.5rem]';
      if (len <= 14) return 'text-[2rem]';
      if (len <= 18) return 'text-[1.5rem]';
      return 'text-[1.25rem]';
    },
    fitAmountDisplay(el: HTMLElement) {
      if (!el) return;
      el.style.fontSize = '';
      if (el.clientWidth === 0) return;

      let fontSize = parseFloat(window.getComputedStyle(el).fontSize);
      while (el.scrollWidth > el.clientWidth && fontSize > 12) {
        fontSize = Math.max(12, fontSize - 2);
        el.style.fontSize = `${fontSize}px`;
      }
    },
    formatCalcPreview(expr: string, currency: string): string {
      const amount = this.evalCalcExpr(expr);
      if (!Number.isFinite(amount)) return '—';
      const formatted = this.isCents(currency)
        ? amount.toFixed(2)
        : Math.round(amount).toLocaleString();
      return `${this.currencySymbol(currency)}${formatted}`;
    },
    toggleCurrency() {
      if (this.form.amount) {
        this.form.amount = '';
        this.showToast('已切換幣別，請重新輸入金額', 'warning');
      }
      this.form.currency = this.nextCurrency(this.form.currency);
      localStorage.setItem('bk_currency', this.form.currency);
      haptic(10);
    },

    // --- Haptic wrapper ---
    haptic(ms: number | number[]) {
      haptic(ms);
    },

    // 冷啟動失敗要提示（Tom 10/16 裁 B）；背景觸發失敗不提示。
    async syncOfflineInBackground(coldStart = false) {
      if (!sessionReload.writesAllowed()) return;
      if (this.authed !== true || isOnline() !== true) return;
      if (coldStart) this.coldStartSyncPending = true;
      // 同步進行中收到的觸發不能丟，否則同步失敗後要等下一個觸發才重送（Tom 10/16）。
      if (offlineQueueDrainPromise) {
        this.syncRerunPending = true;
        return;
      }
      // Only a failed drain clears the flag; a later diagnostics error must not clear a newer cold start's flag.
      const result = await drainOfflineQueue().catch(() => null);
      if (!result) {
        this.coldStartSyncPending = false;
        this.syncRerunPending = false;
        return;
      }
      try {
        const reportAll = this.coldStartSyncPending;
        this.coldStartSyncPending = false;
        const rerun = this.syncRerunPending;
        this.syncRerunPending = false;
        const changed = result.synced + result.quarantinedForeign + result.quarantinedInvalid > 0;
        if (changed) {
          if (this.tab === 'home') this.loadDashboard();
          if (this.tab === 'list') this.loadTransactions();
        }
        const messages = syncResultMessages(result);
        if (messages.length > 0 && (changed || reportAll)) {
          this.showToast(messages.join('；'),
            result.remaining > 0 || result.quarantinedForeign > 0 || result.quarantinedInvalid > 0 ? 'error' : 'success');
        }
        if (rerun && result.remaining > 0) this.syncOfflineInBackground();
        this.diagOfflineCount = result.remaining;
        this.diagUnsyncableCount = (await getUnsyncableItems()).length;
        this.diagOtherHouseholdCount = await getOtherHouseholdCount();
      } catch {}
    },

    // --- Init ---
    async init() {
      await authStore.init();
      // x-effect proved unreliable for re-running the fit on dep changes; $watch is explicit.
      const refitAmountDisplays = () => {
        this.$nextTick(() => {
          this.fitAmountDisplay(document.getElementById('addAmountDisplay') as HTMLElement);
          this.fitAmountDisplay(document.getElementById('editAmountDisplay') as HTMLElement);
        });
      };
      ['form.amount', 'form.currency', 'editForm.amount', 'editForm.currency',
       'showAddSheet', 'showEditSheet'].forEach(path => {
        this.$watch(path, refitAmountDisplays);
      });
      sessionReload.onBlocked(() => { this.sessionChangeBlocked = true; });
      window.addEventListener('resize', refitAmountDisplays);
      window.addEventListener('online', () => {
        this.recoverFromOfflineStartup();
        this.syncOfflineInBackground();
      });
      window.addEventListener('storage', (e) => {
        if (e.key === TOKEN_KEY || e.key === HOUSEHOLD_KEY || e.key === null) {
          sessionReload.request('storage');
        }
      });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.recoverFromOfflineStartup();
          this.syncOfflineInBackground();
        }
      });
      this.$watch('showDiagnostics', async (open: boolean) => {
        this.diagUnsyncableDeleteIds = [];
        if (!open) return;
        try {
          this.diagOfflineCount = await getOfflineQueueCount();
          this.diagUnsyncableCount = (await getUnsyncableItems()).length;
          this.diagOtherHouseholdCount = await getOtherHouseholdCount();
        } catch {
          this.showToast('讀取離線記錄失敗', 'error');
        }
      });

      this.token = authStore.getSession().token || '';

      if (this.token) {
        this.authed = true;
        await this.runStartupLoad();
        this.dismissSplash();
        return;
      }

      // No token — check if we have a household_id saved
      const hid = authStore.getSession().householdId;
      if (hid) {
        // Show PIN screen for this household
        this.onboardingStep = '';
      } else {
        // No household — show onboarding
        this.onboardingStep = 'choose';
      }

      this.dismissSplash();
    },

    reloadAfterSessionChange() {
      sessionReload.reloadNow();
    },

    async runStartupLoad() {
      const generation = ++startupGeneration;
      const session = sessionSnapshot();
      const outcome = await loadStartup({
        fetchCatalog: () => this.fetchCatalog(),
        fetchHousehold: () => this.fetchHousehold(),
        snapshot: sessionSnapshot,
        generation: () => startupGeneration,
      }, { session, generation });
      this.handleStartupOutcome(outcome, session.household);
    },

    handleStartupOutcome(outcome: StartupOutcome<unknown, unknown>, household: string | null) {
      if (outcome.kind === 'ok') {
        try {
          if (!isValidCatalog(outcome.catalog) || !isValidHousehold(outcome.household)) {
            this.enterOfflineStartup(household);
            return;
          }
          this.applyCatalog(outcome.catalog.categories, outcome.catalog.tags);
          this.applyHousehold(outcome.household);
          saveFormCache(localStorageStore(localStorage), household, {
            categories: outcome.catalog.categories, tags: outcome.catalog.tags, members: outcome.household.members,
          });
          this.offlineStartup = false;
          this.formUsable = true;
          this.loadPaymentMethods();
          this.loadDashboard();
          this.loadRecurringRules();
          this.loadKiddoAttention();
          this.initPush();
          if (isOnline()) {
            this.syncOfflineInBackground(true);
            // Sync note history from DB (backfills previously imported data)
            this.syncNoteHistory();
          }
        } catch {
          this.enterOfflineStartup(household);
        }
      } else if (outcome.kind === 'auth') {
        this.clearSessionAndShowPin();
      } else if (outcome.kind === 'offline') {
        this.enterOfflineStartup(household);
      } else if (outcome.kind === 'stale') {
        sessionReload.request('stale');
      }
    },

    enterOfflineStartup(household: string | null) {
      this.offlineStartup = true;
      const saved = loadFormCache(localStorageStore(localStorage), household);
      const cache = isValidCatalog(saved) ? saved : null;
      if (cache && cache.categories.length > 0) {
        this.applyCatalog(cache.categories, cache.tags);
        this.members = cache.members; this.paymentMethods = cache.paymentMethods;
        this.formUsable = true;
      } else {
        this.formUsable = false;
      }
    },

    clearSessionAndShowPin() {
      // Token invalid or TOKEN_UPGRADE_REQUIRED
      void authStore.clearToken();
      this.token = '';
      this.authed = false;
      this.offlineStartup = false;
      this.formUsable = true;
      // Determine: show PIN or onboarding
      const hid = authStore.getSession().householdId;
      if (hid) {
        // Has household — show PIN screen
        this.onboardingStep = '';
      } else {
        this.onboardingStep = 'choose';
      }
    },

    async recoverFromOfflineStartup() {
      if (!this.offlineStartup || !isOnline() || this.offlineStartupRecoveryPromise) return;
      this.offlineStartupRecoveryPromise = (async () => {
        await this.runStartupLoad();
      })();
      try {
        await this.offlineStartupRecoveryPromise;
      } finally {
        this.offlineStartupRecoveryPromise = null;
      }
    },

    dismissSplash() {
      setTimeout(() => {
        const el = document.getElementById('app-loading');
        if (el) { el.classList.add('fade-out'); setTimeout(() => el.remove(), 300); }
      }, 300);
    },

    async fetchCatalog() {
      const [catData, tagData] = await settleAuthFirst([api('/api/categories'), api('/api/tags')]);
      return { categories: catData.categories || [], tags: tagData.tags || [] };
    },

    async loadData() {
      const session = sessionSnapshot();
      const c = await this.fetchCatalog();
      if (!sameSession(session, sessionSnapshot())) return;
      if (!isValidCatalog(c)) throw new Error('目錄資料格式不正確');
      this.applyCatalog(c.categories, c.tags);
    },

    // One implementation for both the server load and the offline cache restore (security review C).
    applyCatalog(categories: any[], tags: any[]) {
      // Build flat subcategory list
      const allSubcategories: any[] = [];
      for (const cat of categories) {
        if (cat.subcategories) {
          allSubcategories.push(...cat.subcategories);
        }
      }

      // Smart default category
      const categoryId = this.form.category_id || getSmartCategoryId(categories);

      this.allCategories = categories;
      this.tags = tags;
      this.allSubcategories = allSubcategories;
      this.form.category_id = categoryId;
    },

    async fetchHousehold() {
      const data = await api('/api/households/me');
      return { name: data.household?.name || '', members: data.members || [], invites: data.invites || [] };
    },

    applyHousehold(h: { name: string; members: any[]; invites: any[] }) {
      this.householdName = h.name;
      this.members = h.members;
      this.invites = h.invites;
    },

    async loadHousehold() {
      const session = sessionSnapshot();
      const h = await this.fetchHousehold();
      if (!sameSession(session, sessionSnapshot())) return;
      if (!isValidHousehold(h)) throw new Error('目錄資料格式不正確');
      this.applyHousehold(h);
    },

    async loadPaymentMethods() {
      const session = sessionSnapshot();
      const household = session.household;
      try {
        const data = await api('/api/payment-methods');
        if (!sameSession(session, sessionSnapshot())) return;
        this.paymentMethods = data.paymentMethods || [];
        saveFormCache(localStorageStore(localStorage), household, { paymentMethods: this.paymentMethods });
      } catch {
        if (!sameSession(session, sessionSnapshot())) return;
        this.paymentMethods = [];
      }
    },

    async savePaymentMethod() {
      const name = this.addPaymentMethodForm.name.trim();
      if (!name) { this.showToast('請填寫名稱', 'error'); return; }
      try {
        await api('/api/payment-methods', {
          method: 'POST',
          body: JSON.stringify({ name, type: this.addPaymentMethodForm.type, member_slug: this.addPaymentMethodForm.member_slug || null }),
        });
        this.showAddPaymentMethod = false;
        this.addPaymentMethodForm = { name: '', type: 'credit_card', member_slug: '' };
        haptic(20);
        this.showToast('已新增', 'success');
        await this.loadPaymentMethods();
      } catch (e: any) {
        this.showToast(e.message || '新增失敗', 'error');
      }
    },

    openEditPaymentMethod(pm: any) {
      this.addPaymentMethodForm = { name: pm.name, type: pm.type, member_slug: pm.member_slug || '' };
      (this as any).editPaymentMethodId = pm.id;
      this.showAddPaymentMethod = true;
    },

    async updatePaymentMethod() {
      const name = this.addPaymentMethodForm.name.trim();
      if (!name) { this.showToast('請填寫名稱', 'error'); return; }
      const id = (this as any).editPaymentMethodId;
      try {
        await api(`/api/payment-methods/${id}`, {
          method: 'PUT',
          body: JSON.stringify({ name, member_slug: this.addPaymentMethodForm.member_slug || null }),
        });
        this.showAddPaymentMethod = false;
        this.addPaymentMethodForm = { name: '', type: 'credit_card', member_slug: '' };
        (this as any).editPaymentMethodId = '';
        haptic(20);
        this.showToast('已更新', 'success');
        await this.loadPaymentMethods();
      } catch (e: any) {
        this.showToast(e.message || '更新失敗', 'error');
      }
    },

    async deletePaymentMethod(id: string) {
      if (!confirm('確定刪除此付款方式？')) return;
      try {
        await api(`/api/payment-methods/${id}`, { method: 'DELETE' });
        haptic(20);
        this.showToast('已刪除', 'success');
        await this.loadPaymentMethods();
      } catch {
        this.showToast('刪除失敗', 'error');
      }
    },

    getPaymentMethodName(id: string): string {
      if (!id) return '現金';
      return this.paymentMethods.find((pm: any) => pm.id === id)?.name || '—';
    },

    getPaymentMethodLabel(pmId: string): string {
      if (!pmId) return '現金';
      const pm = this.paymentMethods.find((p: any) => p.id === pmId);
      if (!pm) return '—';
      if (pm.member_slug) {
        const member = this.members.find((m: any) => m.slug === pm.member_slug);
        return member ? `${member.name}·${pm.name}` : pm.name;
      }
      return pm.name;
    },

    // Weak PIN detection
    isWeakPin(pin: string): string {
      const weak = ['0000','1111','2222','3333','4444','5555','6666','7777','8888','9999',
        '1234','12345','123456','1357','2468','0000','000000','1111','111111',
        '123578','654321','4321','abcd','abcdef','5678','56789','567890'];
      if (weak.includes(pin)) return '這組 PIN 太常見，容易被猜到';
      if (/^(\d)\1+$/.test(pin)) return '全部相同數字容易被猜到';
      if (pin.length < 6) return '請輸入 6 位數字';
      return '';
    },

    async updateHouseholdName() {
      const name = this.householdNameDraft.trim();
      if (!name) { this.showToast('名稱不能空白', 'error'); return; }
      if (name.length > 30) { this.showToast('名稱最長 30 字', 'error'); return; }
      try {
        const data = await api('/api/households/me', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name }),
        });
        if (data.ok) {
          this.householdName = name;
          this.editingHouseholdName = false;
          this.showToast('已更新', 'success');
        } else {
          this.showToast(data.error || '更新失敗', 'error');
        }
      } catch { this.showToast('連線失敗', 'error'); }
    },

    // --- Dashboard ---
    async loadDashboard() {
      const seq = ++this.dashLoadSeq;
      this.dashLoading = true;
      try {
        const [y, m] = currentMonth().split('-');
        const [data, txData] = await Promise.all([
          api(`/api/reports/monthly?year=${y}&month=${m}`),
          api(`/api/transactions?month=${currentMonth()}&limit=5&exclude_recurring=1&date_to=${today()}`),
        ]);
        if (seq !== this.dashLoadSeq) return;

        this.dashboard.monthExpense = data.summary?.expense || 0;
        this.dashboard.monthIncome = data.summary?.income || 0;
        this.dashboard.monthNet = (data.summary?.income || 0) - (data.summary?.expense || 0);
        this.dashboard.topCategories = (data.byCategory || []).filter((c: any) => c.type === 'expense').slice(0, 5);
        this.dashboard.has_fx = data.has_fx || false;

        // Today's expense from daily data (total is already TWD-equivalent via fx_rate)
        const todayStr = today();
        const todayData = (data.daily || []).find((d: any) => d.date === todayStr && d.type === 'expense');
        this.dashboard.todayExpense = Math.round(todayData?.total || 0);
        this.dashboard.todayUsd = todayData?.total_usd || 0;
        // Monthly USD totals for annotations
        this.dashboard.monthExpenseUsd = data.summary?.expense_usd || 0;
        this.dashboard.monthIncomeUsd = data.summary?.income_usd || 0;
        this.dashboard.recent = txData.transactions || [];

        // Render mini chart
        this.$nextTick(() => this.renderDashChart());
      } catch (e) {
        console.error('Dashboard load error:', e);
      } finally {
        if (seq === this.dashLoadSeq) this.dashLoading = false;
      }
    },

    renderDashChart() {
      const canvas = document.getElementById('dashChart') as HTMLCanvasElement;
      if (!canvas) return;
      if (this.dashChartInstance) this.dashChartInstance.destroy();

      const Chart = (window as any).Chart;
      if (!Chart) return;

      const cats = this.dashboard.topCategories.slice(0, 5);
      if (cats.length === 0) return;

      const colors = cats.map((c: any) => CATEGORY_ICONS[c.icon]?.color || '#6B7280');
      this.dashChartInstance = new Chart(canvas, {
        type: 'doughnut',
        data: {
          labels: cats.map((c: any) => c.category_name),
          datasets: [{ data: cats.map((c: any) => c.total), backgroundColor: colors }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          cutout: '60%',
        },
      });
    },

    // --- PIN handling ---
    async handlePinKey(key: any) {
      if (key === 'del') {
        this.pin = this.pin.slice(0, -1);
        this.pinError = '';
        return;
      }
      if (this.pin.length >= 6) return;
      this.pin += String(key);
      this.pinError = '';
      haptic(10);

      if (this.pin.length === 6) {
        await this.tryAuth();
      }
    },

    async tryAuth() {
      try {
        // PIN 只在已綁定的 household 內比對（2026-07-11 P1 修法 A）——
        // 沒綁定的裝置（例：ITP 清掉 localStorage）不打 API，導回 onboarding 並說明。
        const householdId = authStore.getSession().householdId;
        if (!householdId) {
          this.pin = '';
          this.onboardingStep = 'choose';
          this.showToast('此裝置尚未綁定家庭，請用邀請碼加入', 'error');
          return;
        }
        const stillCurrent = authAttempts.begin();
        if (!stillCurrent) {
          this.pin = '';
          this.showToast('登入狀態已在其他分頁變更，請先按「重新載入」', 'error');
          return;
        }
        const resp = await fetch(`${API_BASE}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pin: this.pin, household_id: householdId }),
        });

        const data = await resp.json();
        if (data.ok && data.token) {
          if (!stillCurrent()) {
            this.showToast('另一個分頁已切換登入，這次登入沒有套用，請按「重新載入」', 'error');
            return;
          }
          await authStore.setSession(data.token, data.household_id || householdId);
          this.token = data.token;
          this.authed = true;
          this.onboardingStep = '';
          haptic(20);
          await this.runStartupLoad();
        } else if (data.code === 'RATE_LIMITED') {
          this.pinError = data.error;
          this.pin = '';
          haptic([30, 50, 30]);
        } else {
          this.pinError = data.error || 'PIN 錯誤';
          this.pin = '';
          haptic([30, 50, 30]);
        }
      } catch {
        this.pinError = '無法連線，請稍後再試';
        this.pin = '';
      }
    },

    // --- Tab switching ---
    switchTab(t: string) {
      this.tab = t;
      if (t === 'home') { this.loadDashboard(); this.loadRecurringRules(); }
      if (t === 'list') this.loadTransactions();
      if (t === 'report') this.loadReport();
      if (t === 'settings') { this.loadData(); this.loadHousehold(); this.loadPaymentMethods(); }
    },

    // --- Add sheet ---
    openAddSheet() {
      if (this.offlineStartup && !this.formUsable) { this.showToast('離線中，尚未有可用的分類資料，連線後再記', 'error'); return; }
      this.form.amount = '';
      this.form.description = '';
      this.form.subcategory_id = '';
      this.form.payment_method_id = '';
      this.form.property_tag = '';
      this.form.date = today();
      this.showNoteDate = false;
      // Smart default
      if (this.filteredCategories.length > 0 && !this.form.category_id) {
        this.form.category_id = getSmartCategoryId(this.allCategories);
      }
      if (this.paymentMethods.length === 0) this.loadPaymentMethods();
      this.showAddSheet = true;
      haptic(10);
    },

    // --- Calculator expression evaluator ---
    evalCalcExpr(expr: string): number {
      return evaluateCalcExpression(expr);
    },
    hasCalcOperator(expr: string): boolean {
      return containsCalcOperator(expr);
    },

    // --- Number keypad ---
    keyPress(key: string, target: 'form' | 'editForm' = 'form') {
      haptic(10);
      const f = this[target] as any;
      let amt = String(f.amount || '');
      if (key === 'clear') { f.amount = ''; return; }
      if (key === 'backspace') { f.amount = amt.slice(0, -1); return; }
      if (key === '.') {
        const lastOp = Math.max(amt.lastIndexOf('+'), amt.lastIndexOf('-'), amt.lastIndexOf('×'), amt.lastIndexOf('÷'));
        const seg = lastOp === -1 ? amt : amt.slice(lastOp + 1);
        if (seg.includes('.')) return;
        if (!amt || /[+\-×÷]$/.test(amt)) amt += '0';
        if (amt.length >= 20) return;
        f.amount = amt + '.';
        return;
      }
      if (/^[+\-×÷]$/.test(key)) {
        if (!amt) return;
        const last = amt.slice(-1);
        if (/[+\-×÷]/.test(last)) { f.amount = amt.slice(0, -1) + key; return; }
        if (last === '.') amt = amt.slice(0, -1);
        if (amt.length >= 20) return;
        f.amount = amt + key;
        return;
      }
      const lastOp = Math.max(amt.lastIndexOf('+'), amt.lastIndexOf('-'), amt.lastIndexOf('×'), amt.lastIndexOf('÷'));
      const seg = lastOp === -1 ? amt : amt.slice(lastOp + 1);
      const dotIdx = seg.indexOf('.');
      if (dotIdx !== -1 && seg.length - dotIdx - 1 + key.length > 2) return;
      if (seg.length + key.length > 8) return;
      if (amt.length + key.length > 20) return;
      if (seg === '0' && key === '0') return;
      if (seg === '0' && key !== '00') {
        f.amount = amt.slice(0, -1) + key;
      } else {
        f.amount = amt + key;
      }
    },

    // --- Category selection ---
    selectCategory(cat: any) {
      this.form.category_id = cat.id;
      this.form.subcategory_id = '';
      if (cat.id === 'income_rent') this.form.property_tag = '';
      localStorage.setItem('bk_last_category', cat.id);
    },

    // Edit modal category helpers
    get editFilteredCategories() {
      return this.allCategories.filter((c: any) => c.type === this.editForm.type);
    },
    get editMainCategories() {
      return this.allCategories.filter((c: any) => c.type === this.editForm.type && !c.id.startsWith('kid_'));
    },
    get editKiddoCategories() {
      return this.allCategories.filter((c: any) => c.type === this.editForm.type && c.id.startsWith('kid_'));
    },
    get editSelectedCategorySubs() {
      if (!this.editForm.category_id) return [];
      const cat = this.allCategories.find((c: any) => c.id === this.editForm.category_id);
      return cat?.subcategories || [];
    },
    selectEditCategory(cat: any) {
      this.editForm.category_id = cat.id;
      this.editForm.subcategory_id = '';
      if (cat.id === 'income_rent') this.editForm.property_tag = '';
    },

    // --- Note autocomplete ---
    filterNotes() {
      const q = (this.form.description || '').trim().toLowerCase();
      if (!q) {
        this.noteSuggestions = this.noteHistory.slice(0, 5);
      } else {
        this.noteSuggestions = this.noteHistory
          .filter((n: string) => n.toLowerCase().startsWith(q) && n.toLowerCase() !== q)
          .slice(0, 5);
      }
      this.showNoteSuggestions = this.noteSuggestions.length > 0;
    },
    pickNote(note: string) {
      this.form.description = note;
      this.showNoteSuggestions = false;
    },
    saveNoteHistory(desc: string) {
      if (!desc || !desc.trim()) return;
      const trimmed = desc.trim();
      this.noteHistory = [trimmed, ...this.noteHistory.filter((n: string) => n !== trimmed)].slice(0, 50);
      localStorage.setItem('bk_note_history', JSON.stringify(this.noteHistory));
    },
    async syncNoteHistory() {
      try {
        const data = await api('/api/transactions/notes');
        const dbNotes: string[] = data.notes || [];
        if (dbNotes.length === 0) return;
        const existing = new Set(this.noteHistory as string[]);
        const newNotes = dbNotes.filter((n: string) => !existing.has(n));
        if (newNotes.length > 0) {
          this.noteHistory = [...this.noteHistory, ...newNotes].slice(0, 200);
          localStorage.setItem('bk_note_history', JSON.stringify(this.noteHistory));
        }
      } catch { /* non-critical */ }
    },

    // --- Add-note modal (for add transaction) ---
    openAddNote() {
      this.addNote.text = this.form.description || '';
      this.addNote.suggestions = this.noteHistory.slice(0, 8);
      this.addNote.show = true;
      this.$nextTick(() => {
        const el = document.getElementById('addNoteInput') as HTMLInputElement;
        if (el) el.focus();
      });
    },
    filterAddNotes() {
      const q = (this.addNote.text || '').trim().toLowerCase();
      if (!q) {
        this.addNote.suggestions = this.noteHistory.slice(0, 8);
      } else {
        this.addNote.suggestions = this.noteHistory
          .filter((n: string) => n.toLowerCase().includes(q) && n.toLowerCase() !== q)
          .slice(0, 8);
      }
    },
    confirmAddNote() {
      this.form.description = this.addNote.text.trim();
      this.addNote.show = false;
      haptic(10);
    },

    // --- Save transaction ---
    async saveTransaction() {
      if (this.isSubmitting) return;
      if (!sessionReload.writesAllowed()) {
        sessionReload.markUnsaved();
        this.showToast('登入狀態已變更，這筆沒有存到，內容仍在畫面上', 'error');
        return;
      }
      if (!this.canSave) {
        haptic([30, 50, 30]);
        return;
      }

      this.isSubmitting = true;
      const rawAmount = this.evalCalcExpr(this.form.amount);
      const tx = {
        id: newId(),
        type: this.form.type,
        amount: this.toStoredAmount(rawAmount, this.form.currency),
        currency: this.form.currency,
        category_id: this.form.category_id,
        subcategory_id: this.form.subcategory_id || null,
        description: this.form.description || null,
        date: this.form.date,
        time: currentTime(),
        tags: [...this.form.tags],
        created_by: this.form.created_by,
        payment_method_id: this.form.payment_method_id || null,
        property_tag: this.form.property_tag || null,
        created_at: new Date().toISOString(),
      };

      // Capture the household alongside api's token so a failed POST queues under its original household.
      const household = currentHouseholdId();

      try {
        let successMessage: string | null = '已儲存';
        let save: Promise<Persistence>;
        sessionReload.track(save = (async (): Promise<Persistence> => {
          try {
            if (isOnline()) {
              await api('/api/transactions', { method: 'POST', body: JSON.stringify(tx) });
            } else {
              await enqueueOffline(tx, household);
            }
            return 'persisted';
          } catch {
            if (deletedThisSession.has(tx.id)) {
              // Already handled by the server; queuing it would resurrect a deleted row.
              successMessage = null;
              return 'persisted';
            }
            try {
              await enqueueOffline(tx, household);
              successMessage = '已存至離線佇列';
              return 'persisted';
            } catch {
              return 'unpersisted';
            }
          }
        })());
        const persistence = await save;
        if (persistence === 'unpersisted') {
          this.showToast('儲存失敗，內容仍保留在畫面上，請稍後再試', 'error');
          return;
        }
        if (successMessage === '已儲存') haptic(20);
        if (successMessage) this.showToast(successMessage, 'success');
        this.showAddSheet = false;
        sessionReload.clearUnsaved();
      } finally {
        this.isSubmitting = false;
      }

      // Save note history before reset
      this.saveNoteHistory(this.form.description);

      // Reset form (keep type, currency, member, category)
      this.form.amount = '';
      this.form.description = '';
      this.form.subcategory_id = '';
      this.form.property_tag = '';
      this.form.date = today();

      // Refresh the current tab after saving online or to the offline queue.
      if (this.tab === 'home') this.loadDashboard();
      if (this.tab === 'list') this.loadTransactions();
    },

    // --- List tab ---
    async loadTransactions() {
      const seq = ++this.listLoadSeq;
      const household = currentHouseholdId();
      const filters = {
        month: this.listMonth,
        search: this.searchQuery,
        member: this.memberFilter,
        property: this.propertyFilter,
        household,
      };
      const key = listFilterKey(filters);
      let serverRows: any[] = [];
      const epoch = listCacheEpoch;
      try {
        const params = new URLSearchParams({ month: filters.month, limit: '200' });
        if (filters.search) params.set('search', filters.search);
        if (filters.member) params.set('created_by', filters.member);
        if (filters.property) params.set('property_tag', filters.property);

        const data = await api(`/api/transactions?${params}`);
        serverRows = data.transactions || [];
        // An older load that resolves last must not evict the newer snapshot either (R2 external review P1-2).
        if (epoch === listCacheEpoch && seq === this.listLoadSeq) lastListLoad = { key, rows: serverRows };
      } catch {
        // 篩選相同才沿用上次成功載入的列；換月份或換篩選時不沿用，避免把別的月份顯示在這個月標題下。
        serverRows = serverRowsOnFailure(lastListLoad, key);
      }

      let pendingRows: any[] = [];
      try {
        const db = await openOfflineDB();
        try {
          const items = await new Promise<any[]>((resolve, reject) => {
            const req = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAll();
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
          });
          pendingRows = pendingForList(items, {
            ...filters,
            serverIds: new Set(serverRows.map(tx => tx.id)),
          });
        } finally {
          db.close();
        }
      } catch { /* Keep the server rows if reading the offline queue fails. */ }

      const merged = mergeForList(serverRows, pendingRows);
      // A superseded load does not publish, but still returns what it fetched: exportCSV exports its own load (R3 external review P1-2).
      if (seq !== this.listLoadSeq) return merged;
      this.transactions = merged;
      this.pendingInList = pendingRows.length;
      let income = 0, expense = 0;
      for (const tx of serverRows) {
        if (tx.type === 'income') income += tx.amount;
        else expense += tx.amount;
      }
      this.listSummary = { income, expense, net: income - expense };
      return merged;
    },

    changeMonth(delta: number) {
      const [y, m] = this.listMonth.split('-').map(Number);
      const d = new Date(y, m - 1 + delta, 1);
      this.listMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      this.selectedCalendarDay = '';
      this.loadTransactions();
    },

    formatDateHeader(dateStr: string): string {
      const d = new Date(dateStr + 'T00:00:00');
      const days = ['日', '一', '二', '三', '四', '五', '六'];
      return `${d.getMonth() + 1}/${d.getDate()} (${days[d.getDay()]})`;
    },

    formatShortDate(dateStr: string): string {
      if (!dateStr) return '';
      const t = today();
      if (dateStr === t) return '今天';
      const y = new Date();
      y.setDate(y.getDate() - 1);
      if (dateStr === y.toISOString().slice(0, 10)) return '昨天';
      const d = new Date(dateStr + 'T00:00:00');
      return `${d.getMonth() + 1}/${d.getDate()}`;
    },

    getCategoryIconId(id: string): string {
      return this.allCategories.find((c: any) => c.id === id)?.icon || 'tag';
    },
    getCategoryPath(iconId: string): string {
      return CATEGORY_ICONS[iconId]?.path || CATEGORY_ICONS['tag'].path;
    },
    getCategoryColor(iconId: string): string {
      return CATEGORY_ICONS[iconId]?.color || '#6B7280';
    },
    getCategorySvg(iconId: string, size: string = 'w-5 h-5', color?: string): string {
      const p = CATEGORY_ICONS[iconId]?.path || CATEGORY_ICONS['tag'].path;
      const c = color || CATEGORY_ICONS[iconId]?.color || '#6B7280';
      return `<svg class="${size}" style="color:${c}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="${p}" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/></svg>`;
    },

    getCategoryName(id: string): string {
      return this.allCategories.find((c: any) => c.id === id)?.name || id;
    },

    getSubcategoryName(id: string): string {
      if (!id) return '';
      return this.allSubcategories.find((s: any) => s.id === id)?.name || '';
    },

    // --- Edit modal ---
    editTransaction(tx: any) {
      if (tx._pending) {
        this.showToast('這筆還沒同步，有網路時切回 app 就會送出；同步前不能修改');
        return;
      }
      const currency = tx.currency || 'TWD';
      this.editForm = {
        id: tx.id,
        type: tx.type,
        amount: String(this.isCents(currency) ? tx.amount / 100 : tx.amount),
        currency,
        category_id: tx.category_id,
        subcategory_id: tx.subcategory_id || '',
        description: tx.description || '',
        date: tx.date,
        created_by: tx.created_by || 'shared',
        updated_at: tx.updated_at || '',
        payment_method_id: tx.payment_method_id || '',
        property_tag: tx.property_tag || '',
        recurring_rule_id: tx.recurring_rule_id || null,
      };
      if (this.paymentMethods.length === 0) this.loadPaymentMethods();
      this.showEditSheet = true;
    },

    async updateTransaction() {
      if (!this.canSaveEdit) {
        haptic([30, 50, 30]);
        return;
      }

      try {
        const resolved = this.evalCalcExpr(String(this.editForm.amount));
        const payload = {
          ...this.editForm,
          amount: this.toStoredAmount(resolved, this.editForm.currency),
        };
        const result = await api(`/api/transactions/${this.editForm.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        // Update local updated_at for next edit
        if (result.updated_at) this.editForm.updated_at = result.updated_at;
        this.showEditSheet = false;
        haptic(20);
        this.showToast('已更新', 'success');
        this.loadTransactions();
        if (this.tab === 'home') this.loadDashboard();
      } catch (e: any) {
        if (e.code === 'CONFLICT') {
          // Show conflict modal
          this.showConflictModal = true;
        } else {
          this.showToast('更新失敗', 'error');
        }
      }
    },

    async resolveConflict(choice: 'server' | 'mine') {
      this.showConflictModal = false;
      if (choice === 'server') {
        // Reload from server and close edit
        this.showEditSheet = false;
        this.loadTransactions();
      } else {
        // Force overwrite: send without updated_at check
        if (!this.canSaveEdit) {
          haptic([30, 50, 30]);
          return;
        }

        try {
          const resolved = this.evalCalcExpr(String(this.editForm.amount));
          const payload = {
            ...this.editForm,
            amount: this.toStoredAmount(resolved, this.editForm.currency),
            updated_at: undefined,
          };
          await api(`/api/transactions/${this.editForm.id}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
          });
          this.showEditSheet = false;
          haptic(20);
          this.showToast('已覆蓋更新', 'success');
          this.loadTransactions();
        } catch {
          this.showToast('更新失敗', 'error');
        }
      }
    },

    async deleteCurrentTransaction() {
      const msg = this.editForm.recurring_rule_id
        ? '這是定期交易自動產生的紀錄。\n刪除後本月不會自動重新產生，確定刪除？'
        : '確定刪除這筆交易？';
      if (!confirm(msg)) return;
      // Pin the id before the first await: the sheet can be closed and reopened on another row while the request is in flight (R3 external review P1-1).
      const id = this.editForm.id;
      deletedThisSession.add(id);
      try {
        // Remove the queued copy first. The edit sheet only opens for server rows, so a queued copy with this id is a replay
        // that must not outlive the deletion; if it cannot be removed, the delete is reported as failed and the server row is kept (connector P2 on 3354ac6).
        await removeQueuedItem(id);
        await api(`/api/transactions/${id}`, { method: 'DELETE' });
        this.showEditSheet = false;
        haptic(20);
        this.showToast('已刪除', 'success');
        this.loadTransactions();
        if (this.tab === 'home') this.loadDashboard();
      } catch {
        this.showToast('刪除失敗', 'error');
      }
    },

    // --- Reports ---
    async loadReport() {
      this.drillCategory = '';
      this.drillTransactions = [];
      this.drillMember = '';
      this.drillMemberCat = '';
      this.memberDrillTxs = [];
      try {
        const pmParam = this.reportPaymentFilter ? `&payment_method_id=${this.reportPaymentFilter}` : '';
        if (this.reportType === 'monthly') {
          const [y, m] = this.reportMonth.split('-');
          this.reportData = await api(`/api/reports/monthly?year=${y}&month=${m}${pmParam}`);
        } else {
          this.reportData = await api(`/api/reports/yearly?year=${this.reportYear}`);
        }
        this.$nextTick(() => this.renderChart());
      } catch {
        this.reportData = { summary: { income: 0, expense: 0, net: 0 }, byCategory: [], byMember: [] };
      }
    },

    async toggleCategoryDrill(categoryId: string) {
      if (this.drillCategory === categoryId) {
        this.drillCategory = '';
        this.drillTransactions = [];
        return;
      }
      this.drillCategory = categoryId;
      this.drillLoading = true;
      try {
        const monthParam = this.reportType === 'monthly' ? this.reportMonth : this.reportYear.toString();
        const data = await api(`/api/transactions?month=${monthParam}&category_id=${categoryId}&limit=200`);
        this.drillTransactions = data.transactions || [];
      } catch {
        this.drillTransactions = [];
      }
      this.drillLoading = false;
    },

    toggleMemberDrill(memberSlug: string) {
      this.drillMember = this.drillMember === memberSlug ? '' : memberSlug;
      this.drillMemberCat = '';
      this.memberDrillTxs = [];
    },

    getMemberCategories(memberSlug: string, type: string) {
      if (!this.reportData.byMemberCategory) return [];
      return this.reportData.byMemberCategory.filter(
        (mc: any) => mc.created_by === memberSlug && mc.type === type
      );
    },

    async toggleMemberCategoryDrill(memberSlug: string, categoryId: string) {
      const key = memberSlug + '_' + categoryId;
      if (this.drillMemberCat === key) {
        this.drillMemberCat = '';
        this.memberDrillTxs = [];
        return;
      }
      this.drillMemberCat = key;
      this.memberDrillLoading = true;
      try {
        const monthParam = this.reportType === 'monthly' ? this.reportMonth : this.reportYear.toString();
        const data = await api(`/api/transactions?month=${monthParam}&category_id=${categoryId}&created_by=${memberSlug}&limit=200`);
        this.memberDrillTxs = data.transactions || [];
      } catch {
        this.memberDrillTxs = [];
      }
      this.memberDrillLoading = false;
    },

    getMemberCatSubGroups(memberSlug: string, categoryId: string, type: string): any[] {
      if (this.drillMemberCat !== memberSlug + '_' + categoryId) return [];
      const txs = this.memberDrillTxs;
      // Group by subcategory
      const bySubId: Record<string, any[]> = {};
      for (const tx of txs) {
        const key = tx.subcategory_id || '_none';
        if (!bySubId[key]) bySubId[key] = [];
        bySubId[key].push(tx);
      }
      const subs = (this.reportData.bySubcategory || []).filter(
        (s: any) => s.category_id === categoryId && s.type === type
      );
      if (subs.length === 0) {
        return [{ id: '_flat', name: '', total: 0, transactions: txs }];
      }
      const result: any[] = [];
      for (const s of subs) {
        const stxs = bySubId[s.subcategory_id] || [];
        if (stxs.length > 0) {
          result.push({ id: s.subcategory_id, name: s.subcategory_name, total: stxs.reduce((sum: number, t: any) => sum + this.normAmount(t), 0), transactions: stxs });
        }
      }
      if (bySubId['_none']?.length > 0) {
        result.push({ id: '_none', name: '其他', total: bySubId['_none'].reduce((sum: number, t: any) => sum + this.normAmount(t), 0), transactions: bySubId['_none'] });
      }
      return result;
    },

    getDrillSubcategoryGroups(categoryId: string, type: string): any[] {
      if (this.drillCategory !== categoryId) return [];
      const subs = (this.reportData.bySubcategory || []).filter(
        (s: any) => s.category_id === categoryId && s.type === type
      );
      const catTotal = this.reportData.byCategory?.find(
        (c: any) => c.category_id === categoryId && c.type === type
      )?.total || 0;
      // Group transactions by subcategory_id
      const txBySubId: Record<string, any[]> = {};
      for (const tx of this.drillTransactions) {
        const key = tx.subcategory_id || '_none';
        if (!txBySubId[key]) txBySubId[key] = [];
        txBySubId[key].push(tx);
      }
      if (subs.length === 0) {
        // No subcategories — flat list, no header
        return [{ id: '_flat', name: '', total: 0, pct: 0, transactions: this.drillTransactions }];
      }
      const result: any[] = [];
      for (const s of subs) {
        result.push({
          id: s.subcategory_id,
          name: s.subcategory_name,
          total: s.total,
          count: s.count,
          pct: catTotal > 0 ? Math.round(s.total / catTotal * 100) : 0,
          transactions: txBySubId[s.subcategory_id] || [],
        });
      }
      if (txBySubId['_none']?.length > 0) {
        const noSubTotal = txBySubId['_none'].reduce((sum: number, tx: any) => sum + tx.amount, 0);
        result.push({
          id: '_none', name: '其他', total: noSubTotal, count: txBySubId['_none'].length,
          pct: catTotal > 0 ? Math.round(noSubTotal / catTotal * 100) : 0,
          transactions: txBySubId['_none'],
        });
      }
      return result;
    },

    openQuickNote(tx: any) {
      this.quickNote = { show: true, tx, text: tx.description || '' };
      this.$nextTick(() => {
        const el = document.getElementById('quickNoteInput') as HTMLInputElement;
        if (el) el.focus();
      });
    },

    async saveQuickNote() {
      const tx = this.quickNote.tx;
      if (!tx) return;
      try {
        await api(`/api/transactions/${tx.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: tx.type, amount: tx.amount, currency: tx.currency || 'TWD',
            category_id: tx.category_id, subcategory_id: tx.subcategory_id || null,
            description: this.quickNote.text || null,
            date: tx.date, time: tx.time || null,
            created_by: tx.created_by, updated_at: tx.updated_at,
            payment_method_id: tx.payment_method_id || null,
          }),
        });
        tx.description = this.quickNote.text || null;
        this.quickNote.show = false;
        haptic(10);
      } catch (e: any) {
        alert('儲存失敗');
      }
    },

    changeReportMonth(delta: number) {
      const [y, m] = this.reportMonth.split('-').map(Number);
      const d = new Date(y, m - 1 + delta, 1);
      this.reportMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      this.loadReport();
    },

    renderChart() {
      if (this.chartInstance) {
        if (Array.isArray(this.chartInstance)) this.chartInstance.forEach((c: any) => c.destroy());
        else this.chartInstance.destroy();
      }

      const Chart = (window as any).Chart;
      if (!Chart) return;

      if (this.reportType === 'monthly' && this.reportData.byCategory) {
        const canvas = document.getElementById('reportChartMonthly') as HTMLCanvasElement;
        if (!canvas) return;

        if (this.chartToggle === 'expense') {
          const cats = this.reportData.byCategory.filter((c: any) => c.type === 'expense');
          const colors = cats.map((c: any) => CATEGORY_ICONS[c.icon]?.color || '#6B7280');
          this.chartInstance = new Chart(canvas, {
            type: 'doughnut',
            data: {
              labels: cats.map((c: any) => c.category_name),
              datasets: [{ data: cats.map((c: any) => c.total), backgroundColor: colors }],
            },
            options: { responsive: true, plugins: { legend: { display: false } } },
          });
        } else {
          const cats = this.reportData.byCategory.filter((c: any) => c.type === 'income');
          const incomeColors = ['#10B981', '#14B8A6', '#06B6D4', '#0EA5E9', '#6366F1', '#8B5CF6', '#A855F7', '#D946EF', '#F59E0B'];
          this.chartInstance = new Chart(canvas, {
            type: 'doughnut',
            data: {
              labels: cats.map((c: any) => c.category_name),
              datasets: [{ data: cats.map((c: any) => c.total), backgroundColor: incomeColors.slice(0, cats.length) }],
            },
            options: { responsive: true, plugins: { legend: { display: false } } },
          });
        }
      } else if (this.reportType === 'yearly' && this.reportData.monthly) {
        const canvas = document.getElementById('reportChart') as HTMLCanvasElement;
        if (!canvas) return;
        const months = Array.from({ length: 12 }, (_, i) => `${i + 1}月`);
        const expenseData = new Array(12).fill(0);
        const incomeData = new Array(12).fill(0);

        for (const row of this.reportData.monthly) {
          const monthIdx = parseInt(row.month.split('-')[1]) - 1;
          if (row.type === 'expense') expenseData[monthIdx] = row.total;
          if (row.type === 'income') incomeData[monthIdx] = row.total;
        }

        this.chartInstance = new Chart(canvas, {
          type: 'line',
          data: {
            labels: months,
            datasets: [
              { label: '支出', data: expenseData, borderColor: '#C026D3', backgroundColor: '#C026D320', fill: true, tension: 0.3 },
              { label: '收入', data: incomeData, borderColor: '#10B981', backgroundColor: '#10B98120', fill: true, tension: 0.3 },
            ],
          },
          options: {
            responsive: true,
            plugins: { legend: { position: 'bottom' } },
            scales: { y: { beginAtZero: true } },
          },
        });
      }
    },

    // --- Category management ---
    toggleExpandCategory(id: string) {
      this.expandedCategory = this.expandedCategory === id ? '' : id;
    },

    openAddCategory() {
      this.addCategoryForm = { icon: 'tag', name: '', type: 'expense' };
      this.showAddCategoryModal = true;
    },

    async saveNewCategory() {
      if (!this.addCategoryForm.name || !this.addCategoryForm.icon) {
        this.showToast('請選擇圖示和填寫名稱', 'error');
        return;
      }
      try {
        await api('/api/categories', {
          method: 'POST',
          body: JSON.stringify({
            id: newId(),
            name: this.addCategoryForm.name,
            icon: this.addCategoryForm.icon,
            type: this.addCategoryForm.type,
          }),
        });
        this.showAddCategoryModal = false;
        haptic(20);
        this.showToast('分類已新增', 'success');
        await this.loadData();
      } catch {
        this.showToast('新增失敗', 'error');
      }
    },

    openAddSubcategory(cat: any) {
      this.addSubcategoryForm = {
        category_id: cat.id,
        name: '',
        parentIcon: cat.icon,
        parentName: cat.name,
      };
      this.showAddSubcategoryModal = true;
    },

    async saveNewSubcategory() {
      if (!this.addSubcategoryForm.name) {
        this.showToast('請填寫名稱', 'error');
        return;
      }
      try {
        await api('/api/categories/sub', {
          method: 'POST',
          body: JSON.stringify({
            id: newId(),
            category_id: this.addSubcategoryForm.category_id,
            name: this.addSubcategoryForm.name,
          }),
        });
        this.showAddSubcategoryModal = false;
        haptic(20);
        this.showToast('子分類已新增', 'success');
        await this.loadData();
      } catch {
        this.showToast('新增失敗', 'error');
      }
    },

    // --- Edit / Delete categories ---
    openEditCategory(cat: any) {
      this.editCategoryForm = { id: cat.id, icon: cat.icon, name: cat.name, type: cat.type };
      this.showEditCategoryModal = true;
    },

    async saveEditCategory() {
      if (!this.editCategoryForm.name || !this.editCategoryForm.icon) {
        this.showToast('請選擇圖示和填寫名稱', 'error');
        return;
      }
      try {
        await api(`/api/categories/${this.editCategoryForm.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: this.editCategoryForm.name,
            icon: this.editCategoryForm.icon,
          }),
        });
        this.showEditCategoryModal = false;
        haptic(20);
        this.showToast('分類已更新', 'success');
        await this.loadData();
      } catch {
        this.showToast('更新失敗', 'error');
      }
    },

    async deleteCategory(id: string) {
      if (!confirm('確定刪除此分類？已記錄的交易不受影響。')) return;
      try {
        await api(`/api/categories/${id}`, { method: 'DELETE' });
        haptic(20);
        this.showToast('分類已刪除', 'success');
        await this.loadData();
      } catch {
        this.showToast('刪除失敗', 'error');
      }
    },

    openEditSubcategory(sub: any, cat: any) {
      this.editSubcategoryForm = { id: sub.id, name: sub.name, parentName: cat.name };
      this.showEditSubcategoryModal = true;
    },

    async saveEditSubcategory() {
      if (!this.editSubcategoryForm.name) {
        this.showToast('請填寫名稱', 'error');
        return;
      }
      try {
        await api(`/api/categories/sub/${this.editSubcategoryForm.id}`, {
          method: 'PUT',
          body: JSON.stringify({ name: this.editSubcategoryForm.name }),
        });
        this.showEditSubcategoryModal = false;
        haptic(20);
        this.showToast('子分類已更新', 'success');
        await this.loadData();
      } catch {
        this.showToast('更新失敗', 'error');
      }
    },

    async deleteSubcategory(id: string) {
      if (!confirm('確定刪除此子分類？')) return;
      try {
        await api(`/api/categories/sub/${id}`, { method: 'DELETE' });
        haptic(20);
        this.showToast('子分類已刪除', 'success');
        await this.loadData();
      } catch {
        this.showToast('刪除失敗', 'error');
      }
    },

    // --- Tags ---
    async saveNewTag() {
      if (!this.addTagForm.name) {
        this.showToast('請填寫名稱', 'error');
        return;
      }
      try {
        await api('/api/tags', {
          method: 'POST',
          body: JSON.stringify({
            id: newId(),
            name: this.addTagForm.name,
            color: this.addTagForm.color,
          }),
        });
        this.showAddTag = false;
        haptic(20);
        this.showToast('標籤已新增', 'success');
        await this.loadData();
      } catch {
        this.showToast('新增失敗', 'error');
      }
    },

    async deleteTag(id: string) {
      if (!confirm('確定刪除此標籤？')) return;
      try {
        await api(`/api/tags/${id}`, { method: 'DELETE' });
        this.tags = this.tags.filter((t: any) => t.id !== id);
        this.showToast('已刪除', 'success');
      } catch {
        this.showToast('刪除失敗', 'error');
      }
    },

    // --- Change PIN ---
    async changePin() {
      this.changePinForm.error = '';
      if (!this.changePinForm.old_pin || !this.changePinForm.new_pin) {
        this.changePinForm.error = '請填寫所有欄位';
        return;
      }
      if (this.changePinForm.new_pin.length < 6) {
        this.changePinForm.error = '新 PIN 碼需要 6 位數字';
        return;
      }
      try {
        const data = await api('/api/auth/change-pin', {
          method: 'POST',
          body: JSON.stringify({
            old_pin: this.changePinForm.old_pin,
            new_pin: this.changePinForm.new_pin,
          }),
        });
        if (data.ok) {
          this.showChangePin = false;
          this.changePinForm = { old_pin: '', new_pin: '', error: '' };
          haptic(20);
          this.showToast('PIN 碼已變更', 'success');
        } else {
          this.changePinForm.error = data.error || '變更失敗';
          haptic([30, 50, 30]);
        }
      } catch (e: any) {
        this.changePinForm.error = e.message || '連線失敗';
      }
    },

    // --- Kiddo Parent UI R1 — attention entry point ---
    // Fail-quiet by design: this is a non-blocking home/settings enhancement, not core
    // bookkeeping data. A failure here must never surface an error toast or block init().
    async loadKiddoAttention() {
      this.kiddoLoading = true;
      try {
        const data = await api('/api/kiddo-manage/attention');
        this.kiddoKids = data.kids || [];
        this.kiddoLookbackWeeks = data.lookback_weeks ?? 8;
        this.kiddoReviewStaleDays = data.review_stale_days ?? 8;
        this.kiddoLoaded = true;
      } catch (e) {
        console.warn('loadKiddoAttention failed (non-fatal):', e);
      } finally {
        this.kiddoLoading = false;
      }
    },

    openKiddoPanel() {
      this.showKiddoPanel = true;
      this.loadKiddoAttention();
      this.loadKiddoDisabled();
    },

    // Fail-quiet, same rationale as loadKiddoAttention: a secondary section (list
    // view "已停用" block), not core bookkeeping data — a failure here must not
    // block or error-toast the panel.
    async loadKiddoDisabled() {
      try {
        const data = await api('/api/kiddo-manage/profiles-disabled');
        this.kiddoDisabled = data.profiles || [];
      } catch (e) {
        console.warn('loadKiddoDisabled failed (non-fatal):', e);
      }
    },

    kiddoAvatarEmoji(slug: string): string {
      return KIDDO_AVATAR_OPTIONS.find((a) => a.slug === slug)?.emoji || '🎮';
    },

    // Guard for leaving the 'share' onboarding step: the api_token is shown exactly
    // once (R2/R3 spec — token is never re-displayed) and only lives in JS memory
    // (not persisted), so leaving without a positive "已複製/已分享" confirmation
    // would silently lose it, forcing a token regen (R3, out of scope here).
    kiddoConfirmLeaveShareStep(): boolean {
      if (this.kiddoOnboardStep === 'share' && !this.kiddoOnboardShareConfirmed) {
        return confirm('尚未確認已複製/分享登入碼，這組碼離開後就無法再顯示。確定要離開嗎？');
      }
      return true;
    },

    closeKiddoPanel() {
      if (!this.kiddoConfirmLeaveShareStep()) return;
      this.showKiddoPanel = false;
      this.kiddoOnboardStep = '';
      this.kiddoOnboardResult = null;
      this.kiddoView = 'list';
      this.kiddoDashboard = null;
    },

    // --- Kiddo Parent UI R8 — per-kid dashboard ---
    // Explicit, tap-initiated detail view (unlike loadKiddoAttention's fail-quiet home
    // enhancement): a failure here must be visible with a retry, not swallowed.
    openKiddoDashboard(slug: string) {
      this.kiddoDashboardSlug = slug;
      this.kiddoView = 'dashboard';
      this.kiddoDashboard = null;
      this.resetKiddoAllowanceForm();
      this.resetKiddoManualForm();
      this.resetKiddoReconcileForm();
      this.loadKiddoDashboard();
      this.loadKiddoAllowances();
    },

    async loadKiddoDashboard() {
      if (!this.kiddoDashboardSlug) return;
      this.kiddoDashboardLoading = true;
      this.kiddoDashboardError = false;
      try {
        this.kiddoDashboard = await api('/api/kiddo-manage/dashboard/' + this.kiddoDashboardSlug);
        this.resetKiddoReconcileForm();
      } catch (e) {
        console.warn('loadKiddoDashboard failed:', e);
        this.kiddoDashboardError = true;
      } finally {
        this.kiddoDashboardLoading = false;
      }
    },

    closeKiddoDashboard() {
      this.kiddoView = 'list';
      this.kiddoDashboard = null;
      this.kiddoDashboardSlug = '';
      this.kiddoDashboardError = false;
      this.kiddoTokenActionError = '';
      this.kiddoAllowanceError = '';
      this.kiddoManualError = '';
      this.resetKiddoReconcileForm();
    },

    kiddoWeekdayLabel(day: number): string {
      return ['日', '一', '二', '三', '四', '五', '六'][day] || '一';
    },

    kiddoAllowanceFrequencyLabel(frequency: string): string {
      if (frequency === 'monthly') return '每月';
      if (frequency === 'biweekly') return '每兩週';
      return '每週';
    },

    kiddoAllowanceStatusText(a: any): string {
      if (a.ended_at) return '已結束';
      if (a.paused_at) return '已暫停';
      if (a.last_error) return '發放失敗';
      return '啟用中';
    },

    kiddoAllowanceStatusClass(a: any): string {
      if (a.ended_at) return 'bg-gray-100 text-gray-500';
      if (a.paused_at) return 'bg-amber-50 text-amber-600';
      if (a.last_error) return 'bg-rose-50 text-rose-600';
      return 'bg-emerald-50 text-emerald-600';
    },

    resetKiddoAllowanceForm() {
      this.kiddoAllowanceEditingId = '';
      this.kiddoAllowanceForm = {
        amount: '' as string | number,
        frequency: 'weekly',
        day_of_week: 1,
        day_of_month: 1,
        next_due: today(),
      };
      this.kiddoAllowanceError = '';
    },

    editKiddoAllowance(a: any) {
      this.kiddoAllowanceEditingId = a.id;
      this.kiddoAllowanceForm = {
        amount: a.amount,
        frequency: a.frequency || 'weekly',
        day_of_week: a.day_of_week ?? 1,
        day_of_month: a.day_of_month ?? 1,
        next_due: a.next_due || today(),
      };
      this.kiddoAllowanceError = '';
    },

    resetKiddoManualForm() {
      this.kiddoManualForm = {
        direction: 'give',
        wallet: 'cash',
        amount: '' as string | number,
        note: '',
        client_tx_id: '',
      };
      this.kiddoManualError = '';
    },

    async loadKiddoAllowances() {
      this.kiddoAllowanceLoading = true;
      this.kiddoAllowanceError = '';
      try {
        const data = await api('/api/kiddo-manage/allowances');
        this.kiddoAllowances = data.allowances || [];
      } catch (e: any) {
        console.warn('loadKiddoAllowances failed:', e);
        this.kiddoAllowanceError = e.message || '零用錢載入失敗';
      } finally {
        this.kiddoAllowanceLoading = false;
      }
    },

    async saveKiddoAllowance() {
      const profileId = this.kiddoDashboardProfileId;
      const amount = Number(this.kiddoAllowanceForm.amount);
      if (!profileId || !Number.isFinite(amount) || amount <= 0 || !this.kiddoAllowanceForm.next_due) {
        this.kiddoAllowanceError = '請填寫金額與開始日期';
        return;
      }
      this.kiddoAllowanceSaving = true;
      this.kiddoAllowanceError = '';
      const payload = {
        profile_id: profileId,
        amount,
        frequency: this.kiddoAllowanceForm.frequency,
        day_of_week: Number(this.kiddoAllowanceForm.day_of_week),
        day_of_month: Number(this.kiddoAllowanceForm.day_of_month),
        next_due: this.kiddoAllowanceForm.next_due,
      };
      try {
        if (this.kiddoAllowanceEditingId) {
          await api('/api/kiddo-manage/allowances/' + this.kiddoAllowanceEditingId, {
            method: 'PUT',
            body: JSON.stringify({ ...payload, action: 'update' }),
          });
          this.showToast('零用錢規則已更新', 'success');
        } else {
          await api('/api/kiddo-manage/allowances', {
            method: 'POST',
            body: JSON.stringify(payload),
          });
          this.showToast('零用錢規則已建立', 'success');
        }
        this.resetKiddoAllowanceForm();
        await Promise.all([this.loadKiddoAllowances(), this.loadKiddoDashboard()]);
      } catch (e: any) {
        haptic([30, 50, 30]);
        this.kiddoAllowanceError = e.message || '儲存零用錢失敗';
      } finally {
        this.kiddoAllowanceSaving = false;
      }
    },

    async updateKiddoAllowanceAction(a: any, action: 'pause' | 'resume' | 'end') {
      if (this.kiddoAllowanceSaving) return;
      if (action === 'end' && !confirm('結束後這條規則不會再自動發放。確定結束？')) return;
      this.kiddoAllowanceSaving = true;
      this.kiddoAllowanceError = '';
      try {
        await api('/api/kiddo-manage/allowances/' + a.id, {
          method: 'PUT',
          body: JSON.stringify({ action }),
        });
        haptic(20);
        this.showToast(action === 'pause' ? '已暫停' : (action === 'resume' ? '已恢復' : '已結束'), 'success');
        await Promise.all([this.loadKiddoAllowances(), this.loadKiddoDashboard()]);
      } catch (e: any) {
        haptic([30, 50, 30]);
        this.kiddoAllowanceError = e.message || '更新零用錢狀態失敗';
      } finally {
        this.kiddoAllowanceSaving = false;
      }
    },

    async issueKiddoAllowanceNow(a: any) {
      if (this.kiddoAllowanceSaving) return;
      if (!confirm('立即發放本期會使用與 cron 相同的本期冪等碼，不會與自動發放重複。確定發放？')) return;
      this.kiddoAllowanceSaving = true;
      this.kiddoAllowanceError = '';
      try {
        const result = await api('/api/kiddo-manage/allowances/' + a.id + '/issue-now', { method: 'POST' });
        haptic(20);
        this.showToast(result.duplicate ? '本期已發放過' : '已發放本期零用錢', 'success');
        await Promise.all([this.loadKiddoAllowances(), this.loadKiddoDashboard()]);
      } catch (e: any) {
        haptic([30, 50, 30]);
        this.kiddoAllowanceError = e.message || '立即發放失敗';
      } finally {
        this.kiddoAllowanceSaving = false;
      }
    },

    async submitKiddoManualTransfer() {
      const profileId = this.kiddoDashboardProfileId;
      const amount = Number(this.kiddoManualForm.amount);
      if (!profileId || !Number.isFinite(amount) || amount <= 0) {
        this.kiddoManualError = '請填寫金額';
        return;
      }
      if (!this.kiddoManualForm.client_tx_id) {
        this.kiddoManualForm.client_tx_id = `kiddo_manual:${profileId}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
      }
      this.kiddoManualSaving = true;
      this.kiddoManualError = '';
      try {
        const result = await api('/api/kiddo-manage/allowances/manual', {
          method: 'POST',
          body: JSON.stringify({
            profile_id: profileId,
            direction: this.kiddoManualForm.direction,
            wallet: this.kiddoManualForm.wallet,
            amount,
            note: this.kiddoManualForm.note.trim(),
            client_tx_id: this.kiddoManualForm.client_tx_id,
          }),
        });
        haptic(20);
        this.showToast(result.duplicate ? '這筆已經處理過' : '已記錄', 'success');
        this.resetKiddoManualForm();
        await this.loadKiddoDashboard();
      } catch (e: any) {
        haptic([30, 50, 30]);
        this.kiddoManualError = e.message || '記錄失敗，請重試';
      } finally {
        this.kiddoManualSaving = false;
      }
    },

    resetKiddoReconcileForm() {
      const balances = this.kiddoDashboard?.balances;
      this.kiddoReconcileForm.actual_cash = balances ? Math.round(balances.cash || 0) : '';
      this.kiddoReconcileForm.actual_easycard = balances ? Math.round(balances.easycard || 0) : '';
      this.kiddoReconcileForm.reason = '';
      this.kiddoReconcileForm.client_tx_id = '';
      this.kiddoReconcileError = '';
      this.kiddoReconcileSaving = false;
      this.kiddoReconcileReverseId = '';
    },

    newKiddoReconcileClientTxId(): string {
      const rawRandom = newId();
      const profileKey = this.kiddoDashboardProfileId || this.kiddoDashboardSlug || 'unknown';
      return `kiddo_parent_reconcile:${profileKey}:${Date.now()}:${rawRandom}`.replace(/[^A-Za-z0-9:_-]/g, '_');
    },

    ensureKiddoReconcileClientTxId(): string {
      if (!this.kiddoReconcileForm.client_tx_id) {
        this.kiddoReconcileForm.client_tx_id = this.newKiddoReconcileClientTxId();
      }
      return this.kiddoReconcileForm.client_tx_id;
    },

    formatKiddoSignedAmount(value: number | string | null | undefined): string {
      const n = Number(value || 0);
      const sign = n < 0 ? '-' : '+';
      return `${sign}$${Math.abs(Math.round(n)).toLocaleString()}`;
    },

    kiddoReconcileDiffClass(value: number | null): string {
      if (value === null || value === 0) return 'text-emerald-600 bg-emerald-50';
      return 'text-amber-700 bg-amber-50';
    },

    kiddoReconcileDiffText(wallet: 'cash' | 'easycard'): string {
      const diff = wallet === 'cash' ? this.kiddoReconcileCashDiff : this.kiddoReconcileEasycardDiff;
      if (diff === null) return '請輸入實際金額';
      if (diff === 0) return '相符';
      return `${wallet === 'cash' ? '現金' : '悠遊卡'}調整 ${this.formatKiddoSignedAmount(diff)}`;
    },

    async submitKiddoParentReconcile() {
      if (!this.kiddoDashboardSlug || this.kiddoReconcileSaving) return;
      const actualCash = this.kiddoReconcileActualCashValue;
      const actualEasycard = this.kiddoReconcileActualEasycardValue;
      const reason = this.kiddoReconcileForm.reason.trim();
      if (actualCash === null || actualEasycard === null || actualCash < 0 || actualEasycard < 0) {
        this.kiddoReconcileError = '請輸入有效的實際金額';
        return;
      }
      if (!reason) {
        this.kiddoReconcileError = '請填寫對帳原因';
        return;
      }

      this.kiddoReconcileSaving = true;
      this.kiddoReconcileError = '';
      const clientTxId = this.ensureKiddoReconcileClientTxId();
      try {
        await api('/api/kiddo-manage/reconcile/' + this.kiddoDashboardSlug, {
          method: 'POST',
          body: JSON.stringify({
            actual_cash: actualCash,
            actual_easycard: actualEasycard,
            reason,
            client_tx_id: clientTxId,
          }),
        });
        haptic(20);
        this.showToast('已完成對帳', 'success');
        await this.loadKiddoDashboard();
      } catch (e: any) {
        haptic([30, 50, 30]);
        this.kiddoReconcileError = e.message || '對帳失敗，請重試';
      } finally {
        this.kiddoReconcileSaving = false;
      }
    },

    async reverseKiddoReconcile(rec: any) {
      if (!rec?.id || this.kiddoReconcileReverseId) return;
      if (!confirm('撤銷後會新增反向調整交易，原本的對帳交易仍會保留。確定撤銷？')) return;
      this.kiddoReconcileReverseId = rec.id;
      this.kiddoReconcileError = '';
      try {
        await api('/api/kiddo-manage/reconcile/' + rec.id + '/reverse', {
          method: 'POST',
          body: JSON.stringify({ reason: '家長從 dashboard 撤銷' }),
        });
        haptic(20);
        this.showToast('已撤銷對帳調整', 'success');
        await this.loadKiddoDashboard();
      } catch (e: any) {
        haptic([30, 50, 30]);
        this.kiddoReconcileError = e.message || '撤銷失敗，請重試';
      } finally {
        this.kiddoReconcileReverseId = '';
      }
    },

    kiddoLoginUrl(slug: string, token: string): string {
      return `${API_BASE}/k/${slug}?t=${encodeURIComponent(token)}`;
    },

    kiddoShareText(payload = this.kiddoSharePayload) {
      if (!payload) return '';
      const url = this.kiddoLoginUrl(payload.member_slug, payload.api_token);
      return `${payload.display_name} 的小小理財家登入連結：\n${url}\n\n打開後就能開始記帳。`;
    },

    formatKiddoDateTime(value: string | null | undefined): string {
      if (!value) return '尚未';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      const date = `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
      const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      return `${date} ${time}`;
    },

    openKiddoShareModal(payload: { profile_id: string; member_slug: string; display_name: string; api_token: string; source: 'onboard' | 'rotate' }) {
      this.kiddoSharePayload = payload;
      this.kiddoShareConfirmed = false;
      this.kiddoShareModalOpen = true;
      (this as any).$nextTick(() => this.drawKiddoShareQr());
    },

    drawKiddoShareQr() {
      if (!this.kiddoSharePayload) return;
      const canvas = document.getElementById('kiddo-share-qr') as HTMLCanvasElement | null;
      if (!canvas) return;
      try {
        drawQrToCanvas(canvas, this.kiddoShareUrl);
      } catch (e) {
        console.warn('kiddo QR render failed:', e);
      }
    },

    markKiddoShareConfirmed() {
      this.kiddoShareConfirmed = true;
      if (this.kiddoSharePayload?.source === 'onboard') this.kiddoOnboardShareConfirmed = true;
    },

    closeKiddoShareModal() {
      if (!this.kiddoShareConfirmed) {
        this.showToast('請先勾選已儲存或已分享', 'error');
        return;
      }
      this.kiddoShareModalOpen = false;
      this.kiddoSharePayload = null;
      this.kiddoShareConfirmed = false;
    },

    async copyKiddoShareLink() {
      if (!this.kiddoSharePayload) return;
      try {
        await navigator.clipboard.writeText(this.kiddoShareText());
        this.markKiddoShareConfirmed();
        this.showToast('已複製', 'success');
      } catch {
        this.showToast('複製失敗，請手動選取文字', 'error');
      }
    },

    async shareKiddoLogin() {
      if (!this.kiddoSharePayload || !navigator.share) return;
      try {
        await navigator.share({ text: this.kiddoShareText() });
        this.markKiddoShareConfirmed();
      } catch {
        // User cancelled the share sheet.
      }
    },

    openKiddoTokenConfirm(action: 'rotate' | 'reactivate') {
      if (!this.kiddoDashboardProfileId || !this.kiddoDashboardSlug) {
        this.showToast('找不到小孩帳本，請重新載入', 'error');
        return;
      }
      this.kiddoTokenConfirmAction = action;
      this.kiddoTokenActionError = '';
      this.kiddoTokenConfirmOpen = true;
    },

    async confirmKiddoTokenAction() {
      const profileId = this.kiddoDashboardProfileId;
      const slug = this.kiddoDashboardSlug;
      if (this.kiddoTokenActionLoading || !profileId || !slug) return;
      this.kiddoTokenActionLoading = true;
      this.kiddoTokenActionError = '';
      try {
        const data = await api('/api/kiddo-manage/profiles/' + profileId + '/token', { method: 'POST' });
        const displayName = this.kiddoDashboard?.profile?.display_name ||
          this.kiddoKids.find((k: any) => k.member_slug === slug)?.display_name ||
          '小孩';
        this.kiddoTokenConfirmOpen = false;
        haptic(20);
        // loadKiddoDisabled: reactivate must move this kid out of the 已停用 section
        // (disabled_at cleared server-side by POST /token) so it doesn't linger stale.
        await Promise.all([this.loadKiddoDashboard(), this.loadKiddoAttention(), this.loadKiddoProfiles(), this.loadKiddoDisabled()]);
        this.openKiddoShareModal({
          profile_id: profileId,
          member_slug: slug,
          display_name: displayName,
          api_token: data.api_token,
          source: 'rotate',
        });
        this.showToast(this.kiddoTokenConfirmAction === 'reactivate' ? '已重新啟用，請分享新登入碼' : '已重發新登入碼', 'success');
      } catch (e: any) {
        haptic([30, 50, 30]);
        this.kiddoTokenActionError = e.message || '產生登入碼失敗，請重試';
      } finally {
        this.kiddoTokenActionLoading = false;
      }
    },

    async disableKiddoToken() {
      const profileId = this.kiddoDashboardProfileId;
      if (this.kiddoTokenActionLoading || !profileId) return;
      if (!confirm('停用後，小孩目前的登入連結會立刻失效。之後可用「重新啟用」產生新的登入碼。')) return;
      this.kiddoTokenActionLoading = true;
      this.kiddoTokenActionError = '';
      try {
        await api('/api/kiddo-manage/profiles/' + profileId, { method: 'DELETE' });
        haptic(20);
        // loadKiddoDisabled: the disabled kid must appear in the 已停用 section right
        // away — that section is now the ONLY path back to the dashboard's reactivate
        // button (P1 fix — this kid drops out of kiddoKids the moment it's revoked).
        await Promise.all([this.loadKiddoDashboard(), this.loadKiddoAttention(), this.loadKiddoProfiles(), this.loadKiddoDisabled()]);
        this.showToast('已停用登入', 'success');
      } catch (e: any) {
        haptic([30, 50, 30]);
        this.kiddoTokenActionError = e.message || '停用失敗，請重試';
      } finally {
        this.kiddoTokenActionLoading = false;
      }
    },

    // Human-readable "M/D–M/D 那週" label for a Monday week_start (UTC date math —
    // matches the backend's weekStart()/weekEnd() so labels never drift a day off
    // near month/year boundaries under a non-UTC local timezone).
    kiddoWeekLabel(ws: string): string {
      const start = new Date(ws + 'T00:00:00Z');
      const end = new Date(start.getTime() + 6 * 86400_000);
      const fmt = (d: Date) => `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
      return `${fmt(start)}–${fmt(end)} 那週`;
    },

    // --- Kiddo Parent UI R6 — weekly review detail + approval ---
    // Q6 (SIGNED): parents fix mis-logged transactions in place (main edit UI) rather
    // than a reject/return-for-correction loop, so the only write action here is
    // APPROVED. Explicit, tap-initiated view (like R8 dashboard) — failures must be
    // visible with a retry, never fail-quiet.
    openKiddoReview(slug: string, week: string) {
      this.kiddoDashboardSlug = slug;
      this.kiddoReviewWeek = week;
      this.kiddoView = 'review';
      this.kiddoReviewDetail = null;
      this.kiddoReviewNote = '';
      this.kiddoReviewApproveError = '';
      this.loadKiddoReviewDetail();
    },

    async loadKiddoReviewDetail() {
      if (!this.kiddoDashboardSlug || !this.kiddoReviewWeek) return;
      this.kiddoReviewLoading = true;
      this.kiddoReviewError = false;
      try {
        this.kiddoReviewDetail = await api(
          '/api/kiddo-manage/weekly-review-detail/' + this.kiddoDashboardSlug +
          '?week=' + encodeURIComponent(this.kiddoReviewWeek)
        );
      } catch (e) {
        console.warn('loadKiddoReviewDetail failed:', e);
        this.kiddoReviewError = true;
      } finally {
        this.kiddoReviewLoading = false;
      }
    },

    // Approve the week (+30 XP, streak advance server-side). Idempotency: kiddoReviewApproving
    // gates the button against double-tap; a genuine duplicate (e.g. approved from another
    // device in between) 409s as ALREADY_REVIEWED — surfaced inline, not a silent no-op, and
    // both the home badge (loadKiddoAttention) and this detail view are refreshed either way
    // so a stale "still pending" state can't linger.
    async approveKiddoWeek() {
      if (this.kiddoReviewApproving || !this.kiddoDashboardSlug || !this.kiddoReviewWeek) return;
      this.kiddoReviewApproving = true;
      this.kiddoReviewApproveError = '';
      try {
        const result = await api('/api/kiddo-manage/weekly-review/' + this.kiddoDashboardSlug, {
          method: 'POST',
          body: JSON.stringify({
            status: 'APPROVED',
            week: this.kiddoReviewWeek,
            note: this.kiddoReviewNote.trim() || undefined,
          }),
        });
        haptic(20);
        this.showToast(`已核准，+${result.xp_awarded ?? 30} XP`, 'success');
        await Promise.all([this.loadKiddoAttention(), this.loadKiddoDashboard()]);
        this.closeKiddoReview();
      } catch (e: any) {
        haptic([30, 50, 30]);
        if (e.code === 'CONFLICT') {
          this.kiddoReviewApproveError = '這週已經審核過了（可能已在其他裝置審核），正在重新整理…';
          await Promise.all([this.loadKiddoAttention(), this.loadKiddoDashboard(), this.loadKiddoReviewDetail()]);
        } else {
          this.kiddoReviewApproveError = e.message || '核准失敗，請重試';
        }
      } finally {
        this.kiddoReviewApproving = false;
      }
    },

    closeKiddoReview() {
      this.kiddoView = 'dashboard';
      this.kiddoReviewWeek = '';
      this.kiddoReviewDetail = null;
      this.kiddoReviewNote = '';
      this.kiddoReviewApproveError = '';
    },

    // --- Kiddo Parent UI R2 — onboarding wizard ---
    async loadKiddoProfiles() {
      // Fail-quiet, same rationale as loadKiddoAttention: this only feeds the
      // onboarding activation gauge, never blocks the rest of the UI.
      try {
        const data = await api('/api/kiddo-manage/profiles');
        this.kiddoProfiles = data.profiles || [];
      } catch (e) {
        console.warn('loadKiddoProfiles failed (non-fatal):', e);
      }
    },

    startKiddoOnboard() {
      this.kiddoOnboardStep = 'member';
      this.kiddoOnboardForm = {
        member_slug: '', display_name: '', avatar: 'bear',
        opening_cash: '', opening_easycard: '', as_of_date: today(),
      };
      this.kiddoOnboardShowNewMember = false;
      this.kiddoOnboardNewMemberForm = { name: '' };
      this.kiddoOnboardResult = null;
      this.kiddoOnboardShareConfirmed = false;
      this.kiddoOnboardError = '';
      this.loadKiddoProfiles();
    },

    closeKiddoOnboard() {
      if (!this.kiddoConfirmLeaveShareStep()) return;
      this.kiddoOnboardStep = '';
      this.kiddoOnboardResult = null;
      this.kiddoOnboardError = '';
      this.loadKiddoAttention();
      this.loadKiddoProfiles();
    },

    kiddoOnboardSelectMember(m: any) {
      if (this.kiddoOnboardExistingSlugs.has(m.slug)) return; // already has a profile — UI-level DUPLICATE guard
      this.kiddoOnboardForm.member_slug = m.slug;
      if (!this.kiddoOnboardForm.display_name) this.kiddoOnboardForm.display_name = m.name;
    },

    async kiddoOnboardCreateMember() {
      const name = this.kiddoOnboardNewMemberForm.name.trim();
      if (!name) { this.showToast('請填寫成員名稱', 'error'); return; }
      // Same slugification as addMember() (Member Management section) — kept in
      // sync deliberately rather than extracted, per YAGNI (single 3-line rule, two call sites).
      const raw = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      const slug = /^[a-z0-9][a-z0-9_-]*$/.test(raw) ? raw : 'm_' + Date.now().toString(36).slice(-6);
      try {
        await api('/api/households/members', {
          method: 'POST',
          body: JSON.stringify({ name, slug, color: '#6B7280', avatar_label: name[0] || '' }),
        });
        await this.loadHousehold();
        this.kiddoOnboardForm.member_slug = slug;
        this.kiddoOnboardForm.display_name = name;
        this.kiddoOnboardShowNewMember = false;
        this.kiddoOnboardNewMemberForm = { name: '' };
        haptic(20);
        this.showToast('成員已新增', 'success');
      } catch (e: any) {
        this.showToast(e.message || '新增成員失敗', 'error');
      }
    },

    async createKiddoProfile() {
      if (this.kiddoOnboardSaving) return;
      this.kiddoOnboardSaving = true;
      this.kiddoOnboardError = '';
      try {
        const data = await api('/api/kiddo-manage/profiles', {
          method: 'POST',
          body: JSON.stringify({
            member_slug: this.kiddoOnboardForm.member_slug,
            display_name: this.kiddoOnboardForm.display_name,
            avatar: this.kiddoOnboardForm.avatar,
            opening_cash: Number(this.kiddoOnboardForm.opening_cash) || 0,
            opening_easycard: Number(this.kiddoOnboardForm.opening_easycard) || 0,
            as_of_date: this.kiddoOnboardForm.as_of_date || today(),
          }),
        });
        this.kiddoOnboardResult = data;
        this.kiddoOnboardShareConfirmed = false;
        this.kiddoOnboardStep = 'share';
        haptic(20);
        this.showToast('已建立！請分享登入碼給小孩', 'success');
        await Promise.all([this.loadKiddoAttention(), this.loadKiddoProfiles()]);
        this.openKiddoShareModal({
          profile_id: data.profile_id,
          member_slug: data.member_slug,
          display_name: data.display_name,
          api_token: data.api_token,
          source: 'onboard',
        });
      } catch (e: any) {
        // This is an actively-initiated create action, not a background sync —
        // failures must be visible (toast + inline), never fail-quiet (unlike
        // loadKiddoAttention above).
        const msg = e.message || '建立失敗，請稍後再試';
        this.kiddoOnboardError = msg;
        this.showToast(msg, 'error');
      } finally {
        this.kiddoOnboardSaving = false;
      }
    },

    kiddoOnboardShareText() {
      const r = this.kiddoOnboardResult;
      if (!r) return '';
      return this.kiddoShareText({ ...r, source: 'onboard' });
    },

    openKiddoOnboardShareModal() {
      const r = this.kiddoOnboardResult;
      if (!r) return;
      this.openKiddoShareModal({ ...r, source: 'onboard' });
    },

    async copyKiddoToken() {
      if (!this.kiddoOnboardResult) return;
      try {
        await navigator.clipboard.writeText(this.kiddoOnboardShareText());
        this.kiddoOnboardShareConfirmed = true;
        this.showToast('已複製', 'success');
      } catch {
        this.showToast('複製失敗，請手動選取文字', 'error');
      }
    },

    async shareKiddoToken() {
      if (!this.kiddoOnboardResult || !navigator.share) return;
      try {
        await navigator.share({ text: this.kiddoOnboardShareText() });
        this.kiddoOnboardShareConfirmed = true;
      } catch {
        // User cancelled the share sheet — not an error, no toast.
      }
    },

    async checkKiddoActivation() {
      this.kiddoOnboardCheckingActivation = true;
      try {
        await this.loadKiddoProfiles();
      } finally {
        this.kiddoOnboardCheckingActivation = false;
      }
    },

    // --- Recurring Rules ---
    async loadRecurringRules() {
      try {
        const data = await api('/api/recurring-rules');
        this.recurringRules = data.rules || [];
      } catch {
        this.recurringRules = [];
      }
    },

    resetRecurringForm() {
      this.recurringForm = {
        id: '', type: 'expense', amount: '', currency: 'TWD',
        category_id: '', subcategory_id: '', description: '',
        created_by: 'shared', day_of_month: 1, start_date: today(),
        end_date: '', remaining_count: '', end_mode: 'count',
        skip_short_month: 0, payment_method_id: '',
      };
      this.showRecurringPaymentDropdown = false;
    },

    openAddRecurring() {
      this.resetRecurringForm();
      this.recurringFromAddSheet = false;
      if (this.paymentMethods.length === 0) this.loadPaymentMethods();
      this.showAddRecurring = true;
    },

    openRecurringFromAddSheet() {
      this.resetRecurringForm();
      this.recurringForm.type = this.form.type;
      this.recurringForm.created_by = this.form.created_by;
      this.showAddSheet = false;
      this.recurringFromAddSheet = true;
      this.showAddRecurring = true;
    },

    closeRecurringBackToAdd() {
      this.showAddRecurring = false;
      this.showEditRecurring = false;
      this.recurringFromAddSheet = false;
      this.showAddSheet = true;
    },

    openEditRecurring(rule: any) {
      this.recurringForm = {
        id: rule.id,
        type: rule.type,
        amount: String(this.isCents(rule.currency || 'TWD') ? rule.amount / 100 : rule.amount),
        currency: rule.currency || 'TWD',
        category_id: rule.category_id,
        subcategory_id: rule.subcategory_id || '',
        description: rule.description || '',
        created_by: rule.created_by || 'shared',
        day_of_month: rule.day_of_month,
        start_date: rule.start_date,
        end_date: rule.end_date || '',
        remaining_count: rule.remaining_count ?? '',
        end_mode: rule.remaining_count != null ? 'count' : rule.end_date ? 'date' : 'forever',
        skip_short_month: rule.skip_short_month || 0,
        payment_method_id: rule.payment_method_id || '',
      };
      this.showRecurringPaymentDropdown = false;
      if (this.paymentMethods.length === 0) this.loadPaymentMethods();
      this.showEditRecurring = true;
    },

    async saveRecurringRule() {
      const rawAmt = this.isCents(this.recurringForm.currency)
        ? Math.round((parseFloat(this.recurringForm.amount as string) || 0) * 100)
        : parseInt(this.recurringForm.amount as string);
      if (!rawAmt || rawAmt <= 0 || !this.recurringForm.category_id) {
        this.showToast('請填寫金額和分類', 'error');
        return;
      }
      const payload: any = {
        type: this.recurringForm.type,
        amount: rawAmt,
        currency: this.recurringForm.currency,
        category_id: this.recurringForm.category_id,
        subcategory_id: this.recurringForm.subcategory_id || null,
        description: this.recurringForm.description || null,
        created_by: this.recurringForm.created_by,
        day_of_month: this.recurringForm.day_of_month,
        start_date: this.recurringForm.start_date,
        end_date: this.recurringForm.end_mode === 'date' ? this.recurringForm.end_date : null,
        remaining_count: this.recurringForm.end_mode === 'count' ? parseInt(this.recurringForm.remaining_count as string) || null : null,
        skip_short_month: this.recurringForm.skip_short_month ? 1 : 0,
        payment_method_id: this.recurringForm.payment_method_id || null,
      };
      try {
        if (this.recurringForm.id) {
          await api(`/api/recurring-rules/${this.recurringForm.id}`, {
            method: 'PUT', body: JSON.stringify(payload),
          });
        } else {
          await api('/api/recurring-rules', {
            method: 'POST', body: JSON.stringify(payload),
          });
        }
        const fromAdd = this.recurringFromAddSheet;
        this.showAddRecurring = false;
        this.showEditRecurring = false;
        this.recurringFromAddSheet = false;
        await this.loadRecurringRules();
        haptic(20);
        this.showToast('已儲存', 'success');
        if (fromAdd) this.showAddSheet = true;
      } catch {
        this.showToast('儲存失敗', 'error');
      }
    },

    async deleteRecurringRule(id: string) {
      try {
        await api(`/api/recurring-rules/${id}`, { method: 'DELETE' });
        await this.loadRecurringRules();
        haptic(20);
        this.showToast('已停用', 'success');
      } catch {
        this.showToast('停用失敗', 'error');
      }
    },

    async triggerRecurring() {
      try {
        const data = await api('/api/recurring-rules/trigger', { method: 'POST' });
        await this.loadRecurringRules();
        this.showToast(`已產生 ${data.generated} 筆交易`, 'success');
      } catch {
        this.showToast('觸發失敗', 'error');
      }
    },

    get recurringFilteredCategories() {
      return this.allCategories.filter((c: any) => c.type === this.recurringForm.type);
    },
    get recurringSelectedCategorySubs() {
      if (!this.recurringForm.category_id) return [];
      const cat = this.allCategories.find((c: any) => c.id === this.recurringForm.category_id);
      return cat?.subcategories || [];
    },
    selectRecurringCategory(catId: string) {
      this.recurringForm.category_id = catId;
      this.recurringForm.subcategory_id = '';
    },
    get canSaveRecurring() {
      return this.recurringForm.amount && parseFloat(this.recurringForm.amount as string) > 0 && this.recurringForm.category_id;
    },

    // --- Export CSV ---
    async exportCSV() {
      try {
        // Export what this load fetched, not the shared snapshot: a newer load may have superseded it (R3 external review P1-2).
        const month = this.listMonth; // the file is named after the month this load fetched, not the month at completion (R4 external review P2)
        const loaded = await this.loadTransactions();
        const header = '日期,時間,類型,分類,子分類,金額,幣別,成員,備註\n';
        const serverRows = loaded.filter((tx: any) => !tx._pending);
        const pendingCount = loaded.length - serverRows.length;
        const rows = serverRows.map((tx: any) =>
          `${tx.date},${tx.time || ''},${tx.type === 'expense' ? '支出' : '收入'},${this.getCategoryName(tx.category_id)},${this.getSubcategoryName(tx.subcategory_id)},${tx.amount},${tx.currency || 'TWD'},${this.memberDisplayName(tx.created_by)},${tx.description || ''}`
        ).join('\n');

        const csv = '\uFEFF' + header + rows;
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `記帳_${month}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        this.showToast(pendingCount > 0 ? `已匯出 CSV（${pendingCount} 筆待同步未包含）` : '已匯出 CSV', 'success');
      } catch {
        this.showToast('匯出失敗', 'error');
      }
    },

    async importCSV(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) return;
      input.value = '';

      this.importProgress = '讀取中...';
      try {
        const text = await file.text();
        if (!sessionReload.writesAllowed()) {
          throw Object.assign(new Error('登入狀態已變更'), { code: 'SESSION_CHANGED' });
        }
        this.importProgress = '上傳中...';
        const resp = await fetch(`${API_BASE}/api/import/csv`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': 'text/plain',
          },
          body: text,
        });
        const data = await resp.json() as any;
        if (!resp.ok) throw new Error(data.error || 'Import failed');
        invalidateListCache();

        // Merge imported notes into noteHistory
        const lines = text.split('\n');
        if (lines.length > 1) {
          const header = lines[0].replace(/^\uFEFF/, '');
          const cols = header.split(',');
          const noteIdx = cols.indexOf('備註');
          if (noteIdx >= 0) {
            const existingSet = new Set(this.noteHistory as string[]);
            const newNotes: string[] = [];
            for (let i = 1; i < lines.length; i++) {
              // Simple extraction — note column value
              const parts = lines[i].split(',');
              const note = (parts[noteIdx] || '').trim();
              if (note && !existingSet.has(note)) {
                existingSet.add(note);
                newNotes.push(note);
              }
            }
            if (newNotes.length > 0) {
              this.noteHistory = [...newNotes, ...this.noteHistory].slice(0, 200);
              localStorage.setItem('bk_note_history', JSON.stringify(this.noteHistory));
            }
          }
        }

        this.importProgress = '';
        const dupMsg = data.deduplicated ? `，去重 ${data.deduplicated} 筆` : '';
        this.showToast(`已匯入 ${data.imported} 筆，略過 ${data.skipped} 筆${dupMsg}`, 'success');
        if (this.tab === 'home') this.loadDashboard();
      } catch (err: any) {
        this.importProgress = '';
        this.showToast(err.message || '匯入失敗', 'error');
      }
    },

    // --- Push Notifications ---
    async initPush() {
      this.pushSupported = 'PushManager' in window && 'serviceWorker' in navigator;
      if (!this.pushSupported) return;

      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        this.pushEnabled = !!sub;
      } catch {
        this.pushSupported = false;
      }
    },

    async togglePush() {
      if (!this.pushSupported) return;

      const reg = await navigator.serviceWorker.ready;

      if (this.pushEnabled) {
        // Unsubscribe
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await api('/api/push/subscribe', {
            method: 'DELETE',
            body: JSON.stringify({ endpoint: sub.endpoint }),
          }).catch(() => {});
          await sub.unsubscribe();
        }
        this.pushEnabled = false;
        this.showToast('已關閉推播通知', 'success');
      } else {
        // Subscribe
        const perm = await Notification.requestPermission();
        if (perm !== 'granted') {
          this.showToast('通知權限被拒絕', 'error');
          return;
        }

        try {
          const { publicKey } = await api('/api/push/vapid-key');
          const sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
          });

          const subJson = sub.toJSON();
          await api('/api/push/subscribe', {
            method: 'POST',
            body: JSON.stringify({
              endpoint: subJson.endpoint,
              keys: subJson.keys,
              member: this.form.created_by || 'shared',
            }),
          });

          this.pushEnabled = true;
          this.showToast('推播通知已啟用', 'success');
        } catch (err: any) {
          this.showToast(err.message || '訂閱失敗', 'error');
        }
      }
    },

    // --- Diagnostics ---
    async forceSyncOffline() {
      if (this.diagQueueBusy) return;
      if (!isOnline()) {
        this.showToast('目前離線', 'error');
        return;
      }
      this.diagQueueBusy = true;
      this.diagUnsyncableDeleteIds = [];
      // Only the caller that starts the drain owns it; a joiner (立即同步 during a background sync) leaves the flags to the owner.
      const owner = !offlineQueueDrainPromise;
      try {
        const result = await drainOfflineQueue();
        let rerun = false;
        if (owner) {
          this.coldStartSyncPending = false;
          rerun = this.syncRerunPending;
          this.syncRerunPending = false;
        }
        const messages = syncResultMessages(result);
        this.showToast(messages.join('；') || '無待同步資料',
          result.remaining > 0 || result.quarantinedForeign > 0 || result.quarantinedInvalid > 0 ? 'error' : 'success');
        // The owner refreshes home like a background sync does; the list is refreshed in finally.
        if (owner && this.tab === 'home' && result.synced + result.quarantinedForeign + result.quarantinedInvalid > 0) this.loadDashboard();
        if (rerun && result.remaining > 0) this.syncOfflineInBackground();
      } catch {
        if (owner) {
          this.coldStartSyncPending = false;
          this.syncRerunPending = false;
        }
        this.showToast('同步失敗，請稍後再試', 'error');
      } finally {
        try {
          this.diagOfflineCount = await getOfflineQueueCount();
          this.diagUnsyncableCount = (await getUnsyncableItems()).length;
          this.diagOtherHouseholdCount = await getOtherHouseholdCount();
        } catch {
          this.showToast('讀取離線記錄失敗', 'error');
        }
        this.diagQueueBusy = false;
        if (this.tab === 'list') this.loadTransactions();
      }
    },

    async downloadUnsyncable() {
      try {
        const items = await getUnsyncableItems();
        this.diagUnsyncableCount = items.length;
        this.diagOtherHouseholdCount = await getOtherHouseholdCount();
        if (!items.length) return;
        const blob = new Blob([JSON.stringify(items, null, 2)], { type: 'application/json;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `記帳_無法同步_${today().replace(/-/g, '')}.json`;
        a.click();
        URL.revokeObjectURL(url);
        this.showToast('已下載備份', 'success');
      } catch {
        this.showToast('匯出失敗', 'error');
      }
    },

    async deleteUnsyncable() {
      if (this.diagQueueBusy) return;
      this.diagQueueBusy = true;
      try {
        const items = await getUnsyncableItems();
        this.diagUnsyncableCount = items.length;
        this.diagOtherHouseholdCount = await getOtherHouseholdCount();
        const ids = items.map(item => item.id as string);
        if (!ids.length) {
          this.diagUnsyncableDeleteIds = [];
          return;
        }
        if (ids.length !== this.diagUnsyncableDeleteIds.length ||
            ids.some((id, index) => id !== this.diagUnsyncableDeleteIds[index])) {
          this.diagUnsyncableDeleteIds = ids;
          return;
        }
        this.diagUnsyncableDeleteIds = [];
        const db = await openOfflineDB();
        try {
          await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            for (const id of ids) store.delete(id);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
          });
        } finally {
          db.close();
        }
        this.diagOfflineCount = await getOfflineQueueCount();
        this.diagUnsyncableCount = (await getUnsyncableItems()).length;
        this.diagOtherHouseholdCount = await getOtherHouseholdCount();
        this.showToast(`已刪除 ${ids.length} 筆無法同步記錄`, 'success');
      } catch {
        this.showToast('刪除失敗', 'error');
      } finally {
        this.diagQueueBusy = false;
      }
    },

    // --- Logout ---
    async logout() {
      await authStore.clearToken();
      // Keep bk_household_id so PIN screen knows which household
      this.authed = false;
      this.pin = '';
      this.token = '';
      sessionReload.request('storage');
    },

    // --- Switch Household (full reset) ---
    async switchHousehold() {
      await authStore.clearAll();
      sessionReload.request('storage');
    },

    // --- Onboarding: Create Household ---
    async createHousehold() {
      if (this.isSubmitting) return;
      if (!this.createForm.name || !this.createForm.pin) {
        this.showToast('請填寫家庭名稱和 PIN 碼', 'error');
        return;
      }
      if (this.createForm.pin.length < 6) {
        this.showToast('PIN 碼需要 6 位數字', 'error');
        return;
      }
      const stillCurrent = authAttempts.begin();
      if (!stillCurrent) {
        this.showToast('登入狀態已在其他分頁變更，請先按「重新載入」', 'error');
        return;
      }
      this.isSubmitting = true;
      try {
        const memberNames = [this.createForm.member1, this.createForm.member2].filter(Boolean);
        const resp = await fetch(`${API_BASE}/api/auth/create-household`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: this.createForm.name,
            pin: this.createForm.pin,
            member_names: memberNames,
          }),
        });
        const data = await resp.json();
        if (data.ok && data.token) {
          if (!stillCurrent()) {
            this.showToast('另一個分頁已切換登入；這次建立已在伺服器完成，但本頁沒有套用，請重新載入後再登入', 'error');
            return;
          }
          await authStore.setSession(data.token, data.household_id);
          this.token = data.token;
          this.authed = true;
          this.onboardingStep = '';
          haptic(20);
          await this.runStartupLoad();
        } else {
          this.showToast(data.error || '建立失敗', 'error');
        }
      } catch {
        this.showToast('連線失敗', 'error');
      } finally {
        this.isSubmitting = false;
      }
    },

    // --- Onboarding: Preview Household (by invite code) ---
    async previewHousehold() {
      const code = this.joinForm.invite_code.toUpperCase().trim();
      if (code.length !== 6) {
        this.joinForm.previewError = '邀請碼為 6 位';
        return;
      }
      this.joinForm.previewError = '';
      try {
        const resp = await fetch(`${API_BASE}/api/auth/household-preview?invite_code=${code}`);
        const data = await resp.json();
        if (data.name) {
          this.joinForm.previewName = data.name;
        } else {
          this.joinForm.previewError = data.error || '找不到家庭';
          this.joinForm.previewName = '';
        }
      } catch {
        this.joinForm.previewError = '連線失敗';
      }
    },

    // --- Onboarding: Join Household ---
    async joinHousehold() {
      if (this.isSubmitting) return;
      if (!this.joinForm.invite_code || !this.joinForm.pin) {
        this.showToast('請填寫邀請碼和 PIN 碼', 'error');
        return;
      }
      const stillCurrent = authAttempts.begin();
      if (!stillCurrent) {
        this.showToast('登入狀態已在其他分頁變更，請先按「重新載入」', 'error');
        return;
      }
      this.isSubmitting = true;
      try {
        const resp = await fetch(`${API_BASE}/api/auth/join-household`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            invite_code: this.joinForm.invite_code.toUpperCase().trim(),
            pin: this.joinForm.pin,
          }),
        });
        const data = await resp.json();
        if (data.ok && data.token) {
          if (!stillCurrent()) {
            this.showToast('另一個分頁已切換登入；這次加入已在伺服器完成，但本頁沒有套用，請重新載入後再登入', 'error');
            return;
          }
          await authStore.setSession(data.token, data.household_id);
          this.token = data.token;
          this.authed = true;
          this.onboardingStep = '';
          haptic(20);
          await this.runStartupLoad();
        } else {
          this.showToast(data.error || '加入失敗', 'error');
        }
      } catch {
        this.showToast('連線失敗', 'error');
      } finally {
        this.isSubmitting = false;
      }
    },

    // --- Member Management ---
    async addMember() {
      if (!this.addMemberForm.name) {
        this.showToast('請填寫名稱', 'error');
        return;
      }
      // Generate ASCII slug: strip non-alphanumeric, fallback to timestamp-based ID for CJK names
      const raw = this.addMemberForm.slug || this.addMemberForm.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      const slug = /^[a-z0-9][a-z0-9_-]*$/.test(raw) ? raw : 'm_' + Date.now().toString(36).slice(-6);
      try {
        await api('/api/households/members', {
          method: 'POST',
          body: JSON.stringify({
            name: this.addMemberForm.name,
            slug,
            color: this.addMemberForm.color,
            avatar_label: this.addMemberForm.name[0] || '',
          }),
        });
        this.showAddMember = false;
        this.addMemberForm = { name: '', slug: '', color: '#6B7280' };
        haptic(20);
        this.showToast('成員已新增', 'success');
        await this.loadHousehold();
      } catch (e: any) {
        this.showToast(e.message || '新增失敗', 'error');
      }
    },

    openEditMember(m: any) {
      this.editMemberForm = { id: m.id, name: m.name, color: m.color, avatar_label: m.avatar_label || m.name[0] };
      this.showEditMember = true;
    },

    async saveEditMember() {
      try {
        await api(`/api/households/members/${this.editMemberForm.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: this.editMemberForm.name,
            color: this.editMemberForm.color,
            avatar_label: this.editMemberForm.avatar_label,
          }),
        });
        this.showEditMember = false;
        haptic(20);
        this.showToast('成員已更新', 'success');
        await this.loadHousehold();
      } catch (e: any) {
        this.showToast(e.message || '更新失敗', 'error');
      }
    },

    async deactivateMember(id: string) {
      if (!confirm('確定停用此成員？')) return;
      try {
        await api(`/api/households/members/${id}`, { method: 'DELETE' });
        haptic(20);
        this.showToast('成員已停用', 'success');
        await this.loadHousehold();
      } catch (e: any) {
        this.showToast(e.message || '停用失敗', 'error');
      }
    },

    async createAndShareInvite() {
      try {
        const data = await api('/api/households/invites', { method: 'POST' });
        if (!data.code) {
          this.showToast(data.error || '建立邀請碼失敗', 'error');
          return;
        }
        await this.loadHousehold();
        const text = `加入我的家庭記帳本「${this.householdName}」！\n邀請碼：${data.code}\n（72 小時內有效、限用一次）\n\n👉 https://bookkeeper-8nf.pages.dev`;
        if (navigator.share) {
          try {
            await navigator.share({ title: '家庭記帳邀請', text });
          } catch (e: any) {
            if (e.name !== 'AbortError') this.showToast('分享失敗', 'error');
          }
        } else {
          await navigator.clipboard.writeText(text);
          this.showToast('邀請訊息已複製', 'success');
        }
      } catch {
        this.showToast('建立邀請碼失敗', 'error');
      }
    },

    async revokeInvite(id: string) {
      try {
        await api(`/api/households/invites/${id}`, { method: 'DELETE' });
        this.invites = this.invites.filter((i: any) => i.id !== id);
        this.showToast('邀請碼已撤銷', 'success');
      } catch {
        this.showToast('撤銷失敗', 'error');
      }
    },

    inviteStatus(invite: any): string {
      if (invite.used_at) return 'used';
      if (new Date(invite.expires_at) < new Date()) return 'expired';
      return 'active';
    },

    // --- Toast ---
    showToast(msg: string, type: 'success' | 'error' = 'success') {
      clearTimeout(this.toastTimer);
      this.toast = { show: true, msg, type };
      this.toastTimer = setTimeout(() => { this.toast.show = false; }, 3500);
    },
  }));

Alpine.start();
(window as any).Alpine = Alpine;
